import { splitOfficeAddress } from '../utils/address.js';
import { closeDialog, openDialog } from './dialogs.js';
import { getSessionHash } from './cta-analytics.js';
import { getAttribution } from './attribution.js';
import {
    ensureCurrentOfficeRegistered,
    getCurrentOfficeData,
    getOffice,
    getShortlistIds,
    getShortlistItems,
    registerOffice,
    removeFromShortlist,
    syncShortlistUI,
} from './shortlist-store.js';
import { addDropdownSearch, getDefaultIso } from '../utils/helpers.js';
import { initTooltips } from './tooltips.js';

// Phone country code picker uses the full global sprite (lazy-loaded when form opens)
const PHONE_FLAGS_SPRITE = '/img/sprite-flags.svg?v=2';

// Google Ads conversion tracking
const GOOGLE_CONVERSION_ID = 994029355;
const CONVERSION_LABELS = {
    quote: 'MrIcCJ2G-goQq97-2QM',
    shortlist: 'MrIcCJ2G-goQq97-2QM',
    viewing: 'fWEvCJWH-goQq97-2QM',
    callback: '8WuRCI2I-goQq97-2QM',
    question: 'MrIcCJ2G-goQq97-2QM',
};

function trackLeadEvent(state) {
    try {
        if (typeof window.gtag === 'function') {
            window.gtag('event', 'generate_lead', {
                form_type: state.formType,
                office_entry_id: (state.context?.officeIds || []).join(','),
            });
        }
    } catch (e) {}
}

function trackConversion(formType) {
    const label = CONVERSION_LABELS[formType];
    if (!label || typeof window.gtag !== 'function') return;
    try {
        window.gtag('event', 'conversion', {
            send_to: `AW-${GOOGLE_CONVERSION_ID}/${label}`,
            value: 1.0,
            currency: 'GBP',
        });
    } catch (e) { /* silently fail if gtag not loaded */ }
}

const FORM_HEADINGS = {
    quote: 'Quick Quote',
    viewing: 'Arrange a Viewing',
    shortlist: 'Submit Shortlist',
    callback: 'Request Callback',
    question: 'Ask a Question',
    pdf: 'Download Details',
};

const SUBMIT_LABELS = {
    quote: 'GET QUICK QUOTE',
    viewing: 'SUBMIT REQUEST',
    shortlist: 'SUBMIT SHORTLIST',
    callback: 'REQUEST CALLBACK',
    question: 'SEND MESSAGE',
    pdf: 'SUBMIT & DOWNLOAD PDF',
};

const STARTING_STAGES = {
    quote: 2,
    viewing: 1,
    shortlist: 2,
    callback: 1,
    question: 1,
    pdf: 1,
};

const COMPACT_FORM_MAX_WIDTH = 700;
const COMPACT_FORM_MAX_VISIBLE_HEIGHT = 760;

const COUNTRY_OPTIONS = [
    { iso: 'dz', dial: '+213', label: 'Algeria' },
    { iso: 'ad', dial: '+376', label: 'Andorra' },
    { iso: 'ao', dial: '+244', label: 'Angola' },
    { iso: 'ai', dial: '+1264', label: 'Anguilla' },
    { iso: 'ag', dial: '+1268', label: 'Antigua & Barbuda' },
    { iso: 'ar', dial: '+54', label: 'Argentina' },
    { iso: 'am', dial: '+374', label: 'Armenia' },
    { iso: 'aw', dial: '+297', label: 'Aruba' },
    { iso: 'au', dial: '+61', label: 'Australia' },
    { iso: 'at', dial: '+43', label: 'Austria' },
    { iso: 'az', dial: '+994', label: 'Azerbaijan' },
    { iso: 'bs', dial: '+1242', label: 'Bahamas' },
    { iso: 'bh', dial: '+973', label: 'Bahrain' },
    { iso: 'bd', dial: '+880', label: 'Bangladesh' },
    { iso: 'bb', dial: '+1246', label: 'Barbados' },
    { iso: 'by', dial: '+375', label: 'Belarus' },
    { iso: 'be', dial: '+32', label: 'Belgium' },
    { iso: 'bz', dial: '+501', label: 'Belize' },
    { iso: 'bj', dial: '+229', label: 'Benin' },
    { iso: 'bm', dial: '+1441', label: 'Bermuda' },
    { iso: 'bt', dial: '+975', label: 'Bhutan' },
    { iso: 'bo', dial: '+591', label: 'Bolivia' },
    { iso: 'ba', dial: '+387', label: 'Bosnia Herzegovina' },
    { iso: 'bw', dial: '+267', label: 'Botswana' },
    { iso: 'br', dial: '+55', label: 'Brazil' },
    { iso: 'bn', dial: '+673', label: 'Brunei' },
    { iso: 'bg', dial: '+359', label: 'Bulgaria' },
    { iso: 'bf', dial: '+226', label: 'Burkina Faso' },
    { iso: 'bi', dial: '+257', label: 'Burundi' },
    { iso: 'kh', dial: '+855', label: 'Cambodia' },
    { iso: 'cm', dial: '+237', label: 'Cameroon' },
    { iso: 'ca', dial: '+1', label: 'Canada' },
    { iso: 'cv', dial: '+238', label: 'Cape Verde Islands' },
    { iso: 'ky', dial: '+1345', label: 'Cayman Islands' },
    { iso: 'cf', dial: '+236', label: 'Central African Republic' },
    { iso: 'cl', dial: '+56', label: 'Chile' },
    { iso: 'cn', dial: '+86', label: 'China', tags: 'PRC Chinese' },
    { iso: 'co', dial: '+57', label: 'Colombia' },
    { iso: 'km', dial: '+269', label: 'Comoros' },
    { iso: 'ck', dial: '+682', label: 'Cook Islands' },
    { iso: 'cr', dial: '+506', label: 'Costa Rica' },
    { iso: 'hr', dial: '+385', label: 'Croatia' },
    { iso: 'cu', dial: '+53', label: 'Cuba' },
    { iso: 'cy', dial: '+357', label: 'Cyprus' },
    { iso: 'cz', dial: '+42', label: 'Czechia', tags: 'Czech Republic' },
    { iso: 'cd', dial: '+243', label: 'Democratic Republic of the Congo' },
    { iso: 'dk', dial: '+45', label: 'Denmark' },
    { iso: 'dj', dial: '+253', label: 'Djibouti' },
    { iso: 'dm', dial: '+1809', label: 'Dominica' },
    { iso: 'do', dial: '+1809', label: 'Dominican Republic' },
    { iso: 'ec', dial: '+593', label: 'Ecuador' },
    { iso: 'eg', dial: '+20', label: 'Egypt' },
    { iso: 'ie', dial: '+353', label: 'Ireland', tags: 'Eire Irish Republic' },
    { iso: 'sv', dial: '+503', label: 'El Salvador' },
    { iso: 'gq', dial: '+240', label: 'Equatorial Guinea' },
    { iso: 'er', dial: '+291', label: 'Eritrea' },
    { iso: 'ee', dial: '+372', label: 'Estonia' },
    { iso: 'et', dial: '+251', label: 'Ethiopia' },
    { iso: 'fk', dial: '+500', label: 'Falkland Islands' },
    { iso: 'fo', dial: '+298', label: 'Faroe Islands' },
    { iso: 'fj', dial: '+679', label: 'Fiji' },
    { iso: 'fi', dial: '+358', label: 'Finland' },
    { iso: 'fr', dial: '+33', label: 'France' },
    { iso: 'gf', dial: '+594', label: 'French Guiana' },
    { iso: 'pf', dial: '+689', label: 'French Polynesia' },
    { iso: 'ga', dial: '+241', label: 'Gabon' },
    { iso: 'gm', dial: '+220', label: 'Gambia' },
    { iso: 'ge', dial: '+7880', label: 'Georgia' },
    { iso: 'de', dial: '+49', label: 'Germany', tags: 'Deutschland' },
    { iso: 'gh', dial: '+233', label: 'Ghana' },
    { iso: 'gi', dial: '+350', label: 'Gibraltar' },
    { iso: 'gr', dial: '+30', label: 'Greece' },
    { iso: 'gl', dial: '+299', label: 'Greenland' },
    { iso: 'gd', dial: '+1473', label: 'Grenada' },
    { iso: 'gp', dial: '+590', label: 'Guadeloupe' },
    { iso: 'gu', dial: '+671', label: 'Guam' },
    { iso: 'gt', dial: '+502', label: 'Guatemala' },
    { iso: 'gn', dial: '+224', label: 'Guinea' },
    { iso: 'gw', dial: '+245', label: 'Guinea - Bissau' },
    { iso: 'gy', dial: '+592', label: 'Guyana' },
    { iso: 'ht', dial: '+509', label: 'Haiti' },
    { iso: 'hn', dial: '+504', label: 'Honduras' },
    { iso: 'hk', dial: '+852', label: 'Hong Kong' },
    { iso: 'hu', dial: '+36', label: 'Hungary' },
    { iso: 'is', dial: '+354', label: 'Iceland' },
    { iso: 'in', dial: '+91', label: 'India' },
    { iso: 'id', dial: '+62', label: 'Indonesia' },
    { iso: 'ir', dial: '+98', label: 'Iran' },
    { iso: 'iq', dial: '+964', label: 'Iraq' },
    { iso: 'il', dial: '+972', label: 'Israel' },
    { iso: 'it', dial: '+39', label: 'Italy' },
    { iso: 'ci', dial: '+225', label: 'Ivory Coast', tags: "Cote d'Ivoire Côte" },
    { iso: 'jm', dial: '+1876', label: 'Jamaica' },
    { iso: 'jp', dial: '+81', label: 'Japan' },
    { iso: 'jo', dial: '+962', label: 'Jordan' },
    { iso: 'kz', dial: '+7', label: 'Kazakhstan' },
    { iso: 'ke', dial: '+254', label: 'Kenya' },
    { iso: 'ki', dial: '+686', label: 'Kiribati' },
    { iso: 'kp', dial: '+850', label: 'North Korea', tags: 'Korea DPRK' },
    { iso: 'kr', dial: '+82', label: 'South Korea', tags: 'Korea Republic' },
    { iso: 'kw', dial: '+965', label: 'Kuwait' },
    { iso: 'kg', dial: '+996', label: 'Kyrgyzstan' },
    { iso: 'la', dial: '+856', label: 'Laos' },
    { iso: 'lv', dial: '+371', label: 'Latvia' },
    { iso: 'lb', dial: '+961', label: 'Lebanon' },
    { iso: 'ls', dial: '+266', label: 'Lesotho' },
    { iso: 'lr', dial: '+231', label: 'Liberia' },
    { iso: 'ly', dial: '+218', label: 'Libya' },
    { iso: 'li', dial: '+417', label: 'Liechtenstein' },
    { iso: 'lt', dial: '+370', label: 'Lithuania' },
    { iso: 'lu', dial: '+352', label: 'Luxembourg' },
    { iso: 'mo', dial: '+853', label: 'Macao', tags: 'Macau' },
    { iso: 'mk', dial: '+389', label: 'North Macedonia', tags: 'Macedonia FYROM' },
    { iso: 'mg', dial: '+261', label: 'Madagascar' },
    { iso: 'mw', dial: '+265', label: 'Malawi' },
    { iso: 'my', dial: '+60', label: 'Malaysia' },
    { iso: 'mv', dial: '+960', label: 'Maldives' },
    { iso: 'ml', dial: '+223', label: 'Mali' },
    { iso: 'mt', dial: '+356', label: 'Malta' },
    { iso: 'mh', dial: '+692', label: 'Marshall Islands' },
    { iso: 'mq', dial: '+596', label: 'Martinique' },
    { iso: 'mr', dial: '+222', label: 'Mauritania' },
    { iso: 'yt', dial: '+269', label: 'Mayotte' },
    { iso: 'mx', dial: '+52', label: 'Mexico' },
    { iso: 'fm', dial: '+691', label: 'Micronesia' },
    { iso: 'md', dial: '+373', label: 'Moldova' },
    { iso: 'mc', dial: '+377', label: 'Monaco' },
    { iso: 'mn', dial: '+976', label: 'Mongolia' },
    { iso: 'me', dial: '+382', label: 'Montenegro' },
    { iso: 'ms', dial: '+1664', label: 'Montserrat' },
    { iso: 'ma', dial: '+212', label: 'Morocco' },
    { iso: 'mz', dial: '+258', label: 'Mozambique' },
    { iso: 'mm', dial: '+95', label: 'Myanmar', tags: 'Burma' },
    { iso: 'na', dial: '+264', label: 'Namibia' },
    { iso: 'nr', dial: '+674', label: 'Nauru' },
    { iso: 'np', dial: '+977', label: 'Nepal' },
    { iso: 'nl', dial: '+31', label: 'Netherlands', tags: 'Holland Dutch' },
    { iso: 'nc', dial: '+687', label: 'New Caledonia' },
    { iso: 'nz', dial: '+64', label: 'New Zealand' },
    { iso: 'ni', dial: '+505', label: 'Nicaragua' },
    { iso: 'ne', dial: '+227', label: 'Niger' },
    { iso: 'ng', dial: '+234', label: 'Nigeria' },
    { iso: 'nu', dial: '+683', label: 'Niue' },
    { iso: 'nf', dial: '+672', label: 'Norfolk Islands' },
    { iso: 'mp', dial: '+670', label: 'Northern Marianas' },
    { iso: 'no', dial: '+47', label: 'Norway' },
    { iso: 'om', dial: '+968', label: 'Oman' },
    { iso: 'pw', dial: '+680', label: 'Palau' },
    { iso: 'pa', dial: '+507', label: 'Panama' },
    { iso: 'pg', dial: '+675', label: 'Papua New Guinea' },
    { iso: 'py', dial: '+595', label: 'Paraguay' },
    { iso: 'pe', dial: '+51', label: 'Peru' },
    { iso: 'ph', dial: '+63', label: 'Philippines' },
    { iso: 'pl', dial: '+48', label: 'Poland' },
    { iso: 'pt', dial: '+351', label: 'Portugal' },
    { iso: 'pr', dial: '+1787', label: 'Puerto Rico' },
    { iso: 'qa', dial: '+974', label: 'Qatar' },
    { iso: 'cg', dial: '+242', label: 'Republic of the Congo' },
    { iso: 're', dial: '+262', label: 'Reunion' },
    { iso: 'ro', dial: '+40', label: 'Romania' },
    { iso: 'ru', dial: '+7', label: 'Russia', tags: 'Russian Federation' },
    { iso: 'rw', dial: '+250', label: 'Rwanda' },
    { iso: 'sm', dial: '+378', label: 'San Marino' },
    { iso: 'st', dial: '+239', label: 'Sao Tome & Principe' },
    { iso: 'sa', dial: '+966', label: 'Saudi Arabia' },
    { iso: 'sn', dial: '+221', label: 'Senegal' },
    { iso: 'rs', dial: '+381', label: 'Serbia' },
    { iso: 'sc', dial: '+248', label: 'Seychelles' },
    { iso: 'sl', dial: '+232', label: 'Sierra Leone' },
    { iso: 'sg', dial: '+65', label: 'Singapore' },
    { iso: 'sk', dial: '+421', label: 'Slovak Republic', tags: 'Slovakia' },
    { iso: 'si', dial: '+386', label: 'Slovenia' },
    { iso: 'sb', dial: '+677', label: 'Solomon Islands' },
    { iso: 'so', dial: '+252', label: 'Somalia' },
    { iso: 'za', dial: '+27', label: 'South Africa', tags: 'SA RSA' },
    { iso: 'es', dial: '+34', label: 'Spain' },
    { iso: 'lk', dial: '+94', label: 'Sri Lanka' },
    { iso: 'sh', dial: '+290', label: 'St. Helena' },
    { iso: 'kn', dial: '+1869', label: 'St. Kitts' },
    { iso: 'lc', dial: '+1758', label: 'St. Lucia' },
    { iso: 'sd', dial: '+249', label: 'Sudan' },
    { iso: 'sr', dial: '+597', label: 'Suriname' },
    { iso: 'sz', dial: '+268', label: 'Eswatini', tags: 'Swaziland' },
    { iso: 'se', dial: '+46', label: 'Sweden' },
    { iso: 'ch', dial: '+41', label: 'Switzerland' },
    { iso: 'sy', dial: '+963', label: 'Syria' },
    { iso: 'tw', dial: '+886', label: 'Taiwan', tags: 'ROC Chinese Taipei' },
    { iso: 'tj', dial: '+7', label: 'Tajikstan' },
    { iso: 'tz', dial: '+255', label: 'Tanzania' },
    { iso: 'th', dial: '+66', label: 'Thailand' },
    { iso: 'tg', dial: '+228', label: 'Togo' },
    { iso: 'to', dial: '+676', label: 'Tonga' },
    { iso: 'tt', dial: '+1868', label: 'Trinidad & Tobago' },
    { iso: 'tn', dial: '+216', label: 'Tunisia' },
    { iso: 'tr', dial: '+90', label: 'Türkiye', tags: 'Turkey' },
    { iso: 'tm', dial: '+993', label: 'Turkmenistan' },
    { iso: 'tc', dial: '+1649', label: 'Turks & Caicos Islands' },
    { iso: 'tv', dial: '+688', label: 'Tuvalu' },
    { iso: 'ug', dial: '+256', label: 'Uganda' },
    { iso: 'gb', dial: '+44', label: 'United Kingdom', tags: 'UK Great Britain England British' },
    { iso: 'ua', dial: '+380', label: 'Ukraine' },
    { iso: 'ae', dial: '+971', label: 'United Arab Emirates', tags: 'UAE' },
    { iso: 'uy', dial: '+598', label: 'Uruguay' },
    { iso: 'us', dial: '+1', label: 'United States', tags: 'USA America' },
    { iso: 'uz', dial: '+7', label: 'Uzbekistan' },
    { iso: 'vu', dial: '+678', label: 'Vanuatu' },
    { iso: 'va', dial: '+379', label: 'Vatican City' },
    { iso: 've', dial: '+58', label: 'Venezuela' },
    { iso: 'vn', dial: '+84', label: 'Vietnam' },
    { iso: 'vg', dial: '+1284', label: 'Virgin Islands - British' },
    { iso: 'vi', dial: '+1340', label: 'Virgin Islands - US' },
    { iso: 'wf', dial: '+681', label: 'Wallis & Futuna' },
    { iso: 'ye', dial: '+967', label: 'Yemen' },
    { iso: 'eh', dial: '+212', label: 'Western Sahara' },
    { iso: 'zm', dial: '+260', label: 'Zambia' },
    { iso: 'zw', dial: '+263', label: 'Zimbabwe' },
    { iso: 'ss', dial: '+211', label: 'South Sudan' },
    { iso: 'tl', dial: '+670', label: 'Timor-Leste', tags: 'East Timor' },
    { iso: 'xk', dial: '+383', label: 'Kosovo' },
    { iso: 'pk', dial: '+92', label: 'Pakistan' },
    { iso: 'af', dial: '+93', label: 'Afghanistan' },
    { iso: 'al', dial: '+355', label: 'Albania' },
    { iso: 'td', dial: '+235', label: 'Chad' },
    { iso: 'ws', dial: '+685', label: 'Samoa' },
    { iso: 'mu', dial: '+230', label: 'Mauritius' },
];

// Region-specific priority countries — only countries with listings on the site
// Shown alphabetically at the top of the phone dropdown, all others below
const REGION_PRIORITY = {
    africa: [
        'dz', 'eg', 'gh', 'ke', 'ly', 'mu', 'ma', 'ng', 'za', 'tz', 'tn', 'ug',
    ],
    america: [
        'ar', 'br', 'ca', 'cl', 'co', 'cr', 'ec', 'mx', 'pa', 'pe', 'us',
    ],
    europe: [
        'at', 'be', 'bg', 'hr', 'cy', 'cz', 'dk', 'ee', 'fi', 'fr', 'de', 'gr',
        'hu', 'is', 'ie', 'it', 'lv', 'lt', 'lu', 'mt', 'nl', 'no', 'pl', 'pt',
        'ro', 'rs', 'sk', 'si', 'es', 'se', 'ch', 'tr', 'ua', 'gb',
    ],
    asia: [
        'au', 'bd', 'bn', 'kh', 'cn', 'hk', 'in', 'id', 'jp', 'kr', 'la', 'mo',
        'my', 'mm', 'np', 'nz', 'pk', 'ph', 'sg', 'lk', 'tw', 'th', 'vn',
    ],
    'hong kong': ['cn', 'hk', 'gb', 'us'],
};
const ALWAYS_PRIORITY = [];

const OFFICE_TYPE_PREFIX = '* Regarding office type: ';
const VIEWING_PREFIX = '* Preferred viewing date: ';
const VIEWING_FLEXIBLE_LABEL = 'I\'m flexible';
const VIEWING_FLEXIBLE_MESSAGE = 'Flexible - please suggest a date and time';
const VIEWING_CHEVRON_LEFT = '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M10 3 5 8l5 5"/></svg>';
const VIEWING_CHEVRON_RIGHT = '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M6 3l5 5-5 5"/></svg>';
const FAILED_RESPONSE = `
    <div class="response response--failed">
        <div>
            <h3>Sorry, that didn't go through</h3>
            <h4>Your enquiry hasn't been sent</h4>
            <p>This usually happens when the page has been open for a while. Refresh and try again; the details you've entered will be kept.</p>
            <a href="#" class="form-retry" data-form-retry>Refresh and try again</a>
        </div>
    </div>
`;

const DEFAULT_RESPONSE = `
    <div class="response">
        <div>
            <h3>Thank you</h3>
            <h4>Your enquiry has been received</h4>
            <p>Our advisory team will be in touch shortly with availability, pricing and viewing options.</p>
        </div>
        <p class="closing-in">This window will close in <span class="cDown">9</span> seconds.</p>
    </div>
`;

let activeForm = null;
let compactViewportSyncBound = false;

export function initForms() {
    registerPageOffices();
    ensureCurrentOfficeRegistered();
    bindCompactViewportSync();
    bindKeyboardResize();

    // Start IP country detection early so it's ready before any form opens
    ensureUserCountryDetected();

    document.addEventListener('click', handleFormTriggerClick);

    // /enquiries?entry_id=X&form_type=... bounces here as ?enquire=quote|viewing (see enquiries/index.html).
    // Open the matching modal, then drop the parameter so reloads and shares stay clean.
    openEnquiryFromQuery();

    // Intercept PDF download links on detail pages — show enquiry form first
    // (skip if user already submitted a form this session, or if it's the success screen link)
    if (document.body.id === 'details') {
        document.addEventListener('click', (event) => {
            const pdfLink = event.target.closest('a[href*="/pdf/office"]');
            if (pdfLink && !pdfLink.classList.contains('pdf-download-link')) {
                try { if (sessionStorage.getItem('oe_form_submitted')) return; } catch(e) {}
                event.preventDefault();
                const pdfUrl = pdfLink.getAttribute('href');
                showEnquiryForm('pdf', { pdfUrl });
            }
        });
    }

    const dialog = document.getElementById('form-dialog');
    if (dialog) {
        dialog.addEventListener('close', resetFormState);
    }

    initStickyCta();
}

/**
 * Scroll a focused input toward the top of its scrollable stage,
 * leaving maximum room below for the submit button and Go back.
 */
function scrollFieldToTop(input) {
    const stage = input.closest('.qq-stage');
    if (!stage) return;
    // Find all inputs in this stage
    const inputs = [...stage.querySelectorAll('input, textarea, select')];
    const idx = inputs.indexOf(input);
    const isLastInput = idx === inputs.length - 1;
    // Only scroll to show submit button when focusing the last input
    if (isLastInput) {
        stage.scrollTo({ top: stage.scrollHeight - 15, behavior: 'smooth' });
    }
}

/**
 * Mobile keyboard resize handler.
 * On iOS Safari the keyboard does NOT shrink 100dvh — the layout viewport
 * stays full-size and the page scrolls behind the keyboard. We use
 * visualViewport to detect this and update --dialog-viewport-height so
 * the entire dialog + wrapper chain resizes automatically.
 * We also add a .keyboard-open class so CSS can make stages scrollable.
 */
function bindKeyboardResize() {
    if (!window.visualViewport) return;

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    let fullHeight = window.visualViewport.height;
    let wasKeyboardOpen = false;

    function update() {
        const dialog = document.getElementById('form-dialog');
        if (!dialog || !dialog.open) return;

        const vv = window.visualViewport;
        const keyboardOpen = vv.height < fullHeight * 0.85;

        if (keyboardOpen) {
            // On non-iOS, manually adjust dialog height and position
            // (iOS handles this natively via 100dvh)
            if (!isIOS) {
                document.documentElement.style.setProperty(
                    '--dialog-viewport-height', vv.height + 'px'
                );
                dialog.style.top = vv.offsetTop + 'px';
            }
            dialog.classList.add('keyboard-open');

            // On non-iOS, scroll after the dialog has resized
            if (!isIOS) {
                const focused = dialog.querySelector('input:focus, select:focus, textarea:focus');
                if (focused) {
                    requestAnimationFrame(() => scrollFieldToTop(focused));
                }
            }
            wasKeyboardOpen = true;
        } else {
            if (!isIOS) {
                document.documentElement.style.removeProperty('--dialog-viewport-height');
                dialog.style.top = '';
            }
            dialog.classList.remove('keyboard-open');
            wasKeyboardOpen = false;
            fullHeight = vv.height;
        }
    }

    // On non-iOS, scroll to focused input when tapping between fields.
    // Use a delay so the scroll happens after the keyboard has settled.
    if (!isIOS) {
        document.addEventListener('focusin', (e) => {
            const dialog = document.getElementById('form-dialog');
            if (!dialog || !dialog.open) return;
            const input = e.target.closest('input, select, textarea');
            if (!input || !dialog.contains(input)) return;
            setTimeout(() => scrollFieldToTop(input), 400);
        });
    }

    window.visualViewport.addEventListener('resize', update);
    window.visualViewport.addEventListener('scroll', update);
}

/**
 * Sticky mobile CTA — shows at 880px and below on details pages,
 * replacing the chatbot. Fades in on first scroll or after 5 seconds.
 */
function initStickyCta() {
    if (document.body.id !== 'details') return;

    // Find the primary CTA to clone its data attributes
    const primaryCta = document.querySelector('.btn-cta-primary[data-form-type="quote"]');
    if (!primaryCta) return;

    const btn = document.createElement('button');
    btn.className = 'sticky-cta';
    btn.type = 'button';
    btn.textContent = 'Check Availability Now';

    // Copy data attributes so the form trigger handler works
    btn.dataset.formType = 'quote';
    btn.dataset.entryId = primaryCta.dataset.entryId || '';
    btn.dataset.category = 'Quote Bottom';
    btn.dataset.city = primaryCta.dataset.city || '';
    btn.dataset.countryCode = primaryCta.dataset.countryCode || '';
    btn.dataset.countryName = primaryCta.dataset.countryName || '';
    btn.dataset.officeTitle = primaryCta.dataset.officeTitle || '';

    document.body.appendChild(btn);

    let shown = false;
    const show = () => {
        if (shown) return;
        shown = true;
        btn.classList.add('is-visible');
    };

    // Show on first scroll
    window.addEventListener('scroll', () => show(), { once: true, passive: true });

    // Or after 5 seconds
    setTimeout(show, 5000);
}

function registerPageOffices() {
    document.querySelectorAll('[data-entry-id][data-office-title]').forEach((trigger) => {
        registerOffice({
            entryId: trigger.dataset.entryId,
            title: trigger.dataset.officeTitle,
            location: trigger.dataset.officeLocation,
            image: trigger.dataset.officeImage,
            href: trigger.dataset.officeHref,
        });
    });
}

function openEnquiryFromQuery() {
    try {
        const params = new URLSearchParams(window.location.search);
        const type = params.get('enquire');
        if (type !== 'quote' && type !== 'viewing') return;
        const trigger = document.querySelector(`[data-form-type="${type}"][data-entry-id]`);
        if (!trigger) return;
        params.delete('enquire');
        const query = params.toString();
        window.history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash);
        window.setTimeout(() => trigger.click(), 300);
    } catch (e) { /* ignore */ }
}

function handleFormTriggerClick(event) {
    if (event.target.closest('#form-dialog')) {
        return;
    }

    const trigger = event.target.closest('[data-form-type]:not(.get-details)');
    if (!trigger) {
        return;
    }

    event.preventDefault();
    showEnquiryForm(trigger.dataset.formType || 'quote', {
        entryId: trigger.dataset.entryId,
        locationLabel: trigger.dataset.locationLabel || '',
        category: trigger.dataset.category || '',
        city: trigger.dataset.city || '',
        countryName: trigger.dataset.countryName || '',
    });
}

// Lazy geoinfo fetch — fires once per session when a form opens
var geoinfoPending = null;

function ensureUserCountryDetected() {
    // Check for IP-detected country specifically (not page-context country)
    try {
        const ipCountry = localStorage.getItem('newofficeplease_ip_country');
        if (ipCountry && ipCountry !== 'undefined') return; // Already have real IP country
    } catch(e) {}
    if (geoinfoPending) return; // Already fetching

    geoinfoPending = fetch('/ajax/geoinfo')
        .then(r => r.text())
        .then(raw => {
            var parts = raw.trim().toLowerCase().split('|');
            var code = parts[0];
            var source = parts[1] || 'unknown';
            if (code && code !== 'error') {
                try {
                    localStorage.setItem('newofficeplease_country_code', code);
                    localStorage.setItem('newofficeplease_ip_country', code);
                } catch(e) {}
                // Update any open phone country select (both hidden select and visual trigger)
                if (activeForm?.form) {
                    const select = activeForm.form.querySelector('#qq-country-code-select2-flags');
                    if (select) {
                        const userOption = COUNTRY_OPTIONS.find(o => o.iso === code);
                        if (userOption) {
                            select.value = userOption.dial;
                            select.dataset.selectedIso = userOption.iso;
                            applyCountryFlag(select, userOption.iso);
                            // Also update the visual trigger button
                            const trigger = select.closest('.select2-country-flags')?.querySelector('.country-select-trigger');
                            if (trigger) {
                                updateCountrySelectTrigger(trigger, userOption.dial, userOption.iso);
                            }
                        }
                    }
                }
            }
        })
        .catch(() => {});
}

export function showEnquiryForm(type = 'quote', context = {}) {
    ensureCurrentOfficeRegistered();

    // Trigger country detection early so it's ready by the phone stage
    ensureUserCountryDetected();

    // Preload the splash animation so it's cached before the user reaches that stage
    new Image().src = '/img/generating-quotes-animated.png';

    const dialog = document.getElementById('form-dialog');
    const content = dialog?.querySelector('.form-dialog-content');
    if (!dialog || !content) {
        return;
    }

    resetFormState();

    const normalizedContext = buildContext(type, context);
    dialog.classList.toggle('form-dialog--wide', type === 'shortlist');
    dialog.classList.toggle('form-dialog--viewing', type === 'viewing');
    content.innerHTML = buildLegacyMarkup(type, normalizedContext);
    attachShortlistImageFallbacks(content.querySelector('#shortlist-panel'));

    activeForm = {
        dialog,
        content,
        container: content.querySelector('.get-details'),
        wrapper: content.querySelector('#enquiry-wrapper'),
        form: content.querySelector('form'),
        quickQuote: content.querySelector('#quick-quote'),
        formType: type,
        context: normalizedContext,
        currentStage: STARTING_STAGES[type] || 2,
        startingStage: STARTING_STAGES[type] || 2,
        totalStages: 6,
        countdownTimer: null,
        splashTimer: null,
        _openedAt: Date.now(),
        _maxStage: STARTING_STAGES[type] || 2,
    };

    setupFormBehavior(activeForm);
    syncShortlistPanelHeight(content.querySelector('#shortlist-panel'));
    openDialog(dialog, { autoFocus: false });
    syncCompactFormMode(activeForm);

    // Restore any saved draft from a previous close
    restoreFormDraft(activeForm);

    // GTM dataLayer event
    if (window.dataLayer) {
        window.dataLayer.push({ event: 'linkClicked', linkType: type });
    }
}

export function renderFormInContainer(container, type = 'quote', context = {}, options = {}) {
    ensureCurrentOfficeRegistered();

    resetFormState();

    const normalizedContext = buildContext(type, context);
    const markup = buildLegacyMarkup(type, normalizedContext);
    container.innerHTML = markup;
    attachShortlistImageFallbacks(container.querySelector('#shortlist-panel'));

    const getDetails = container.querySelector('.get-details');
    if (getDetails) {
        getDetails.classList.add('chatbot-form-mode');
    }

    // Add chatbot formType hidden field
    const formTypeField = container.querySelector('.formType');
    if (formTypeField) {
        formTypeField.value = 'tnon-chatbot';
    }

    activeForm = {
        dialog: null,
        content: container,
        container: getDetails,
        wrapper: container.querySelector('#enquiry-wrapper'),
        form: container.querySelector('form'),
        quickQuote: container.querySelector('#quick-quote'),
        formType: type,
        context: normalizedContext,
        currentStage: STARTING_STAGES[type] || 2,
        startingStage: STARTING_STAGES[type] || 2,
        totalStages: 6,
        countdownTimer: null,
        splashTimer: null,
        _openedAt: Date.now(),
        _maxStage: STARTING_STAGES[type] || 2,
        onClose: options.onClose || null,
        isChatbotMode: true,
    };

    setupFormBehavior(activeForm);
    syncCompactFormMode(activeForm);
}

function buildContext(type, context) {
    const currentOffice = getCurrentOfficeData();
    const pageType = document.body.id; // 'details', 'listing', 'home', 'inner', 'guides'
    const isDetailsPage = pageType === 'details';
    const isListingPage = pageType === 'listing';
    const hasOffice = currentOffice !== null;
    const shortlistIds = type === 'shortlist' ? getShortlistIds() : [];

    // On listing pages, grab the first office card's data as fallback
    let listingFirstOffice = null;
    if (isListingPage) {
        const firstCard = document.querySelector('.office-card--listing[data-entry-id]');
        if (firstCard) {
            listingFirstOffice = {
                entryId: firstCard.dataset.entryId,
                title: firstCard.dataset.officeName || firstCard.querySelector('.office-card-title')?.textContent?.trim() || '',
            };
        }
    }

    // Get the listing city name from the page title
    const listingCity = isListingPage
        ? (document.querySelector('.listing-page-title span')?.textContent?.trim() || context.locationLabel || '')
        : '';

    const requestedIds =
        context.officeIds ||
        context.office_ids ||
        (context.entryId ? [context.entryId] : null) ||
        (type === 'shortlist' ? shortlistIds
            : (isDetailsPage && hasOffice) ? [currentOffice.entryId]
            : listingFirstOffice ? [listingFirstOffice.entryId]
            : []);

    const officeIds = requestedIds?.length ? requestedIds.map((id) => String(id)) : ((isDetailsPage && hasOffice) ? [currentOffice.entryId] : []);
    const offices = officeIds
        .map((entryId) => getOffice(entryId) || (hasOffice && entryId === currentOffice.entryId ? currentOffice : null))
        .filter(Boolean);

    const officeNames = offices.map((office) => office.title);
    const firstOffice = offices[0] || (isDetailsPage && hasOffice ? currentOffice : {});
    const moreCount = Math.max(officeNames.length - 1, 0);

    // Two-line header address (bold building/street, lighter area and city) on details pages
    const pageAddress = getDetailsPageAddress();
    const wantsHeaderAddress = isDetailsPage && offices.length <= 1 && !context.locationLabel && firstOffice.title;
    const isPageOffice = hasOffice && String(firstOffice.entryId || '') === String(currentOffice.entryId || '');
    const headerAddress = wantsHeaderAddress
        ? ((isPageOffice && getDetailsPageTitleLines())
            || splitOfficeAddress(firstOffice.title, context.city || pageAddress.city, context.countryName || pageAddress.country))
        : null;

    // Build subtitle based on page context
    let subtitle;
    if (context.locationLabel) {
        subtitle = context.locationLabel.replace(/(\w)-(\w)/g, '$1 $2');
    } else if (officeNames.length && officeNames[0] && isDetailsPage) {
        subtitle = `${officeNames[0]}${moreCount ? ` (+${moreCount} more)` : ''}`;
    } else if (isListingPage && listingCity) {
        subtitle = listingCity;
    } else {
        subtitle = "We'll connect you with the right workspace";
    }

    // City: on details use office location, on listing use city name, otherwise country
    const city = isDetailsPage && hasOffice
        ? (firstOffice.location || currentOffice.location || getContextCity())
        : isListingPage
            ? (listingCity || getContextCity())
            : getContextCity();

    // Office interest: on details use office name, on listing use first office + city
    const officeInterest = (isDetailsPage && hasOffice)
        ? (officeNames.join(', ') || currentOffice.title)
        : isListingPage && listingFirstOffice
            ? listingFirstOffice.title
            : '';

    return {
        officeIds,
        officeNames,
        offices,
        currentOffice,
        subtitle,
        officeInterest,
        city,
        headerAddress,
        country: getEntryCountryIso() || getContextCountryIso(),
        thumbImage: (isDetailsPage && hasOffice) ? (firstOffice.image || currentOffice.image) : '',
        locationLabel: context.locationLabel || '',
        viewingCity: context.city || '',
        sourceUrl: context.sourceUrl || window.location.href,
        reference: `${officeIds.length ? officeIds.join(',') : 'general'}_${Date.now()}`,
        pdfUrl: context.pdfUrl || '',
        category: context.category || '',
    };
}

function setupFormBehavior(state) {
    bindCloseBehavior(state);
    initializeStageLayout(state);
    initializeFloatingInputs(state);
    initializeCountrySelect(state);
    initializeViewingField(state);
    initializeTooltipPopups(state);
    initTooltips();
    bindLegacyEvents(state);
    bindAdminTestFill(state);
    updateShortlistMeta(state);
    updateStageControls(state, state.currentStage);
    syncCompactFormMode(state);
}

function bindCompactViewportSync() {
    if (compactViewportSyncBound) {
        return;
    }

    compactViewportSyncBound = true;
    const syncActiveFormCompactMode = () => {
        if (activeForm) {
            syncCompactFormMode(activeForm);
        }
    };

    window.addEventListener('resize', syncActiveFormCompactMode, { passive: true });
    window.addEventListener('orientationchange', syncActiveFormCompactMode, { passive: true });

    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', syncActiveFormCompactMode, { passive: true });
        window.visualViewport.addEventListener('scroll', syncActiveFormCompactMode, { passive: true });
    }
}

function syncCompactFormMode(state) {
    if (!state?.container) {
        return;
    }

    const shouldUseCompactMode = isCompactFormViewport();
    state.container.classList.toggle('get-details--dialog--compact', shouldUseCompactMode);
    state.dialog?.classList.toggle('form-dialog--compact', shouldUseCompactMode);
}

function isCompactFormViewport() {
    const viewportWidth = Math.round(
        window.visualViewport?.width
        || window.innerWidth
        || document.documentElement.clientWidth
    );
    const viewportHeight = Math.round(
        window.visualViewport?.height
        || window.innerHeight
        || document.documentElement.clientHeight
    );

    return viewportWidth <= COMPACT_FORM_MAX_WIDTH && viewportHeight <= COMPACT_FORM_MAX_VISIBLE_HEIGHT;
}

function toggleShortlistPanel(state, shouldOpen) {
    if (state.formType !== 'shortlist' || !state.wrapper) {
        return;
    }

    const nextOpen = Boolean(shouldOpen);
    state.wrapper.classList.toggle('shortlist-mobile-open', nextOpen);

    const toggle = state.container.querySelector('.shortlist-edit-toggle');
    if (toggle) {
        toggle.setAttribute('aria-expanded', String(nextOpen));
    }
}

function updateShortlistMeta(state, items = getShortlistItems()) {
    if (state.formType !== 'shortlist') {
        return;
    }

    const count = items.length;
    state.container.querySelectorAll('.shortlist-count-badge, .shortlist-edit-count').forEach((target) => {
        target.textContent = count ? String(count) : '';
    });

    const toggle = state.container.querySelector('.shortlist-edit-toggle');
    if (toggle) {
        toggle.hidden = count < 2;
    }
}

function bindCloseBehavior(state) {
    state.container?.querySelectorAll('.close-form').forEach((button) => {
        button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (state.isChatbotMode) {
                const wrapper = state.content?.closest('.chatbot-enquiry-form-wrapper');
                if (wrapper) wrapper.remove();
                if (state.onClose) state.onClose();
            } else {
                closeDialog(state.dialog);
            }
        });
    });
}

function initializeStageLayout(state) {
    state.form.querySelectorAll('.qq-stage').forEach((stage) => {
        const stageNumber = Number(stage.dataset.qqStage);
        stage.classList.toggle('qq-included-panel', stageNumber >= state.startingStage);
    });

    activateStage(state, state.currentStage, { animateSplash: false });
}

function initializeFloatingInputs(state) {
    state.form.querySelectorAll('.qq-text-input input, .qq-text-input textarea').forEach((input) => {
        const holder = input.closest('.qq-text-input');
        if (input.value.trim()) {
            holder?.classList.add('had-focus');
        }

        input.addEventListener('focus', () => {
            holder?.classList.add('had-focus');
        });

        input.addEventListener('blur', () => {
            if (!input.value.trim()) {
                holder?.classList.remove('had-focus');
            }
        });

        input.addEventListener('input', () => {
            holder?.classList.toggle('had-focus', Boolean(input.value.trim()));
            holder?.classList.remove('qq-error');
            const dynamicMessage = holder?.querySelector('.q-error-message[data-generated="true"]');
            dynamicMessage?.remove();
            updateStageControls(state, state.currentStage);
        });
    });
}

function initializeCountrySelect(state) {
    const select = state.form.querySelector('#qq-country-code-select2-flags');
    if (!select) {
        return;
    }

    const defaultCountry = getDefaultCountryOption();
    select.value = defaultCountry.dial;
    select.dataset.selectedIso = defaultCountry.iso;
    enhanceCountrySelect(select, defaultCountry.iso);

    select.addEventListener('change', () => {
        const selectedIso = getSelectedCountryIso(select, defaultCountry.iso);
        applyCountryFlag(select, selectedIso);
    });

    const phoneHolder = state.form.querySelector('.qq-telephone-field-holder');
    phoneHolder?.classList.add('had-focus-permenant');
}

function initializeViewingField(state) {
    const field = state.form.querySelector('#datetimepicker');
    if (!field) {
        return;
    }

    const picker = state.form.querySelector('[data-viewing-picker]');
    if (!picker) {
        return;
    }

    const minDate = getInitialViewingDate();
    const initialDay = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
    const initialTime = `${String(minDate.getHours()).padStart(2, '0')}:00`;

    const viewingState = {
        minDate,
        visibleMonth: new Date(minDate.getFullYear(), minDate.getMonth(), 1),
        selectedDay: initialDay,
        selectedTime: initialTime,
        hasExplicitSelection: false,
        dayChosen: false,
    };
    state.viewingState = viewingState;

    const clickScope = picker.closest('.select-viewing-date') || picker;
    clickScope.addEventListener('click', (event) => {
        const prevButton = event.target.closest('[data-viewing-prev]');
        if (prevButton) {
            const previousMonth = new Date(viewingState.visibleMonth);
            previousMonth.setMonth(previousMonth.getMonth() - 1);
            const minMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
            if (previousMonth >= minMonth) {
                viewingState.visibleMonth = previousMonth;
                renderViewingPicker(state, viewingState);
            }
            return;
        }

        const nextButton = event.target.closest('[data-viewing-next]');
        if (nextButton) {
            const nextMonth = new Date(viewingState.visibleMonth);
            nextMonth.setMonth(nextMonth.getMonth() + 1);
            viewingState.visibleMonth = nextMonth;
            renderViewingPicker(state, viewingState);
            return;
        }

        const resetButton = event.target.closest('[data-viewing-reset]');
        if (resetButton) {
            viewingState.visibleMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
            viewingState.selectedDay = new Date(initialDay);
            viewingState.selectedTime = initialTime;
            viewingState.hasExplicitSelection = false;
            viewingState.dayChosen = false;
            viewingState.timeTapped = false;
            renderViewingPicker(state, viewingState);
            syncViewingSelection(state, viewingState);
            return;
        }

        const dayButton = event.target.closest('[data-viewing-day]');
        if (dayButton && !dayButton.disabled) {
            const day = Number(dayButton.dataset.viewingDay);
            viewingState.selectedDay = new Date(
                viewingState.visibleMonth.getFullYear(),
                viewingState.visibleMonth.getMonth(),
                day,
            );
            if (!isViewingTimeValid(viewingState, minDate)) {
                viewingState.selectedTime = null;
            }
            viewingState.dayChosen = true;
            viewingState.hasExplicitSelection = Boolean(viewingState.selectedTime);
            renderViewingPicker(state, viewingState);
            syncViewingSelection(state, viewingState);
            return;
        }

        const scrollButton = event.target.closest('[data-viewing-time-scroll]');
        if (scrollButton) {
            const direction = Number(scrollButton.dataset.viewingTimeScroll || '0');
            const timesContainer = picker.querySelector('[data-viewing-times]');
            if (timesContainer) {
                const step = (viewingState.timePageSize || 1) * (viewingState.timePitch || 120);
                timesContainer.scrollBy({
                    left: direction * step,
                    behavior: 'smooth',
                });
            }
            return;
        }

        const timeButton = event.target.closest('[data-viewing-time]');
        if (timeButton && !timeButton.disabled) {
            viewingState.selectedTime = timeButton.dataset.viewingTime;
            viewingState.timeTapped = true;
            // A time on its own is not a slot: the visitor still has to tap a date
            viewingState.hasExplicitSelection = Boolean(viewingState.dayChosen && viewingState.selectedDay && viewingState.selectedTime);
            renderViewingPicker(state, viewingState);
            syncViewingSelection(state, viewingState);
            return;
        }
    });

    const timesContainer = picker.querySelector('[data-viewing-times]');
    timesContainer?.addEventListener('scroll', () => updateViewingTimeScrollState(picker), { passive: true });

    const timesWrap = picker.querySelector('.viewing-times-wrap');
    if (timesWrap && 'ResizeObserver' in window) {
        new ResizeObserver(() => layoutViewingTimes(picker, viewingState)).observe(timesWrap);
    }

    renderViewingPicker(state, viewingState);
    syncViewingSelection(state, viewingState);
}

function updateViewingTimeScrollState(picker) {
    const wrap = picker.querySelector('.viewing-times-wrap');
    const times = picker.querySelector('[data-viewing-times]');
    if (!wrap || !times) {
        return;
    }

    const maxScroll = times.scrollWidth - times.clientWidth;
    wrap.classList.toggle('is-at-start', times.scrollLeft <= 2);
    wrap.classList.toggle('is-at-end', times.scrollLeft >= maxScroll - 2);
}

function initializeTooltipPopups(state) {
    state.form.querySelectorAll('.pop-qmark').forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
            event.preventDefault();
            const textInput = trigger.closest('.has-info');
            textInput?.classList.toggle('pop-open');
        });
    });

    state.form.querySelectorAll('.close-pop').forEach((button) => {
        button.addEventListener('click', (event) => {
            event.preventDefault();
            button.closest('.has-info')?.classList.remove('pop-open');
        });
    });
}

function bindLegacyEvents(state) {
    state.container.addEventListener('click', (event) => {
        const shortlistToggle = event.target.closest('.shortlist-edit-toggle');
        if (shortlistToggle) {
            event.preventDefault();
            toggleShortlistPanel(state, !state.wrapper?.classList.contains('shortlist-mobile-open'));
            return;
        }

        const shortlistClose = event.target.closest('.shortlist-panel-close');
        if (shortlistClose) {
            event.preventDefault();
            toggleShortlistPanel(state, false);
            return;
        }

        const removeButton = event.target.closest('.shortlisted-property-remove, .shortlisted-property-remove-link');
        if (removeButton) {
            event.preventDefault();
            handleShortlistRemoval(state, removeButton.dataset.entryId);
            return;
        }
    });

    state.form.addEventListener('click', (event) => {
        // Failed send: keep what they typed and reload for a fresh session
        const retry = event.target.closest('[data-form-retry]');
        if (retry) {
            event.preventDefault();
            saveFormDraft();
            window.location.reload();
            return;
        }

        // PDF proceed button — go to next stage (people count)
        const proceedBtn = event.target.closest('.pdf-proceed-btn');
        if (proceedBtn) {
            event.preventDefault();
            activateStage(state, 2, { animateSplash: false });
            return;
        }

        // PDF skip button — just download the PDF and close
        const skipBtn = event.target.closest('.pdf-skip-btn');
        if (skipBtn) {
            event.preventDefault();
            const pdfUrl = state.context?.pdfUrl;
            if (pdfUrl) window.open(pdfUrl, '_blank');
            closeDialog(state.dialog);
            return;
        }

        const option = event.target.closest('.qq-stage a[data-value]');
        if (option) {
            event.preventDefault();
            handleOptionSelection(state, option);
            return;
        }

        const goBack = event.target.closest('.qq-go-back');
        if (goBack) {
            event.preventDefault();
            handleGoBack(state);
            return;
        }

        const nextButton = event.target.closest('.qq-next:not(.qq-submit)');
        if (nextButton) {
            event.preventDefault();
            handleGoNext(state);
            return;
        }

        const submitButton = event.target.closest('.qq-submit');
        if (submitButton) {
            event.preventDefault();
            handleLegacySubmit(state);
            return;
        }
    });

    state.form.addEventListener('submit', (event) => {
        event.preventDefault();
    });

    state.form.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.tagName !== 'TEXTAREA') {
            event.preventDefault();
            if (state.currentStage < state.totalStages) {
                handleGoNext(state);
            } else {
                handleLegacySubmit(state);
            }
        }
    });
}

function setupCallbackBehavior(state) {
    initializeFloatingInputs(state);
    bindAdminTestFill(state);

    const form = state.form;
    form.addEventListener('click', (event) => {
        const submitButton = event.target.closest('.qq-submit');
        if (submitButton) {
            event.preventDefault();
            submitCallbackForm(state);
        }
    });

    form.addEventListener('submit', (event) => {
        event.preventDefault();
        submitCallbackForm(state);
    });
}

function applyOptionSelection(state, option) {
    const stage = option.closest('.qq-stage');
    if (!stage) {
        return false;
    }

    const targetField = stage.dataset.targetField;
    stage.querySelectorAll('a[data-value]').forEach((link) => link.classList.remove('qq-selected'));
    option.classList.add('qq-selected');

    if (targetField && targetField !== 'office-type') {
        const targetInput = state.form.querySelector(`#${CSS.escape(targetField)}`);
        if (targetInput) {
            targetInput.value = option.dataset.value || '';
        }
    }

    return true;
}

function handleOptionSelection(state, option) {
    if (!applyOptionSelection(state, option)) {
        return;
    }

    window.setTimeout(() => {
        if (state.currentStage < state.totalStages) {
            activateStage(state, state.currentStage + 1, { animateSplash: true });
        }
    }, 500);
}

function handleGoBack(state) {
    if (state.currentStage <= state.startingStage) {
        return;
    }

    activateStage(state, state.currentStage - 1, { animateSplash: false });
}

function handleGoNext(state) {
    if (!validateStage(state, state.currentStage)) {
        return;
    }

    if (state.currentStage < state.totalStages) {
        activateStage(state, state.currentStage + 1, { animateSplash: true });
    }
}

function handleLegacySubmit(state) {
    if (!validateStage(state, state.totalStages)) {
        return;
    }

    const questionField = state.form.querySelector('#qq-question');
    const officeType = state.form.querySelector('.qq-type-values .qq-selected span')?.textContent?.trim();
    if (officeType && questionField && !questionField.value.includes(OFFICE_TYPE_PREFIX)) {
        questionField.value = `${OFFICE_TYPE_PREFIX}${officeType} *\n${questionField.value}`;
    }

    combinePhone(state.form);
    submitFormRequest(state, state.form);
}

function submitCallbackForm(state) {
    const callbackStage = state.form.querySelector('.qq-stage');
    if (!validateFields(callbackStage)) {
        return;
    }

    submitFormRequest(state, state.form);
}

function submitFormRequest(state, form) {
    const overlay = form.querySelector('.form-submitting');
    if (!overlay) {
        return;
    }

    overlay.style.display = 'block';
    overlay.innerHTML = '<p>Your enquiry is being processed<br>Please wait...</p>';

    try {
        const sessionHashInput = form.querySelector('[name="session_hash"]');
        if (sessionHashInput) {
            sessionHashInput.value = getSessionHash();
        }
    } catch(e) {}

    const formData = new FormData(form);
    const attribution = getAttribution();
    formData.set('referrer', attribution.referrer);
    formData.set('landing', attribution.landing);
    const action = window.FRED_SEND_URL || form.getAttribute('action');

    const shouldPost = window.location.protocol === 'http:' || window.location.protocol === 'https:';
    const request = shouldPost
        ? fetch(action, {
            method: 'POST',
            body: formData,
        }).then(async (response) => {
            if (!response.ok) {
                throw new Error(`Form submission failed with status ${response.status}`);
            }
            return response.text();
        })
        : Promise.resolve('');

    request
        .then((html) => {
            // Google Ads conversion tracking
            trackConversion(state.formType);
            trackLeadEvent(state);

            // Clear saved form draft and flag so resetFormState doesn't re-save
            clearFormDraft();
            state._submitted = true;
            try { sessionStorage.setItem('oe_form_submitted', '1'); } catch(e) {}

            // GTM dataLayer event for successful submission
            if (window.dataLayer) {
                window.dataLayer.push({ event: 'Enquiry_submitted', formType: state.formType });
            }

            // Track form progress immediately on submission — don't wait for dialog close
            // (user may navigate away before the countdown finishes)
            if (state._openedAt) {
                const timeInForm = Math.round((Date.now() - state._openedAt) / 1000);
                const maxStage = state._maxStage || state.currentStage;
                const formFields = extractFormFields(state.form);
                import('./cta-analytics.js').then(m => m.trackFormProgress(maxStage, timeInForm, false, formFields));
            }

            window.setTimeout(() => {
                overlay.innerHTML = html?.trim() || DEFAULT_RESPONSE;

                if (state.formType === 'pdf' && state.context?.pdfUrl) {
                    // PDF form: replace the closing countdown with a styled download link
                    const closingEl = overlay.querySelector('.closing-in');
                    if (closingEl) {
                        const pdfUrl = state.context.pdfUrl;
                        closingEl.innerHTML = `<a href="${pdfUrl}" target="_blank" class="pdf-download-link" style="display:flex; align-items:center; margin:16px auto 0; padding:14px 20px; background:#fff; color:#091650; text-decoration:none; border-radius:8px; font-weight:600; font-size:16px; border:1px solid #091650; width:100%; max-width:320px; box-sizing:border-box; transition:box-shadow 0.2s ease;">
                            <svg style="width:25px; height:25px; flex-shrink:0; stroke:currentColor; fill:none; stroke-width:2;" aria-hidden="true"><use href="/img/sprite-general.svg#icon-download"></use></svg>
                            <span style="flex:1; text-align:center;">Download your PDF</span>
                        </a>
                        <style>.pdf-download-link:hover{box-shadow:0 0 0 2px #091650 !important;}</style>`;
                        closingEl.style.textAlign = 'center';
                        // Open PDF first, then close dialog after a delay
                        closingEl.querySelector('.pdf-download-link')?.addEventListener('click', (e) => {
                            // Let the link open naturally (target=_blank), then close dialog
                            state._submitted = true; // Prevent draft re-save
                            setTimeout(() => closeDialog(state.dialog), 1500);
                        });
                    }
                } else {
                    startClosingCountdown(state, overlay);
                }
            }, 1000);
        })
        .catch((error) => {
            // The send was refused (most often an expired session after the page sat open for hours).
            // Say so honestly rather than showing the thank-you, and keep the draft so a refresh loses nothing.
            if (window.dataLayer) {
                window.dataLayer.push({ event: 'Enquiry_failed', formType: state.formType, reason: error?.message || 'network' });
            }
            window.setTimeout(() => {
                overlay.innerHTML = FAILED_RESPONSE;
            }, 1000);
        });
}

function startClosingCountdown(state, overlay) {
    const counter = overlay.querySelector('.cDown');
    if (!counter) {
        return;
    }

    let remaining = 10;
    counter.textContent = String(remaining);
    window.clearInterval(state.countdownTimer);

    state.countdownTimer = window.setInterval(() => {
        remaining -= 1;
        counter.textContent = String(Math.max(remaining, 0));

        if (remaining <= 0) {
            window.clearInterval(state.countdownTimer);
            if (state.isChatbotMode) {
                const wrapper = state.content?.closest('.chatbot-enquiry-form-wrapper');
                if (wrapper) wrapper.remove();
                if (state.onClose) state.onClose();
            } else {
                closeDialog(state.dialog);
            }
        }
    }, 1300);
}

function activateStage(state, newStage, options = {}) {
    const previousStage = state.currentStage;
    const stageElements = [...state.form.querySelectorAll('.qq-stage')];
    const splash = state.form.querySelector('.qq-gen-quote-splash');
    const shouldAnimateSplash = options.animateSplash && newStage === 5 && newStage > previousStage && state.formType !== 'callback' && state.formType !== 'question' && state.formType !== 'pdf';

    const applyStageChange = () => {
        stageElements.forEach((stage) => {
            const stageNumber = Number(stage.dataset.qqStage);
            const isActive = stageNumber === newStage;
            stage.classList.toggle('qq-active-panel', isActive);
            stage.style.left = `${(stageNumber - newStage) * 100}%`;

            // Trap tab focus within the active panel
            stage.querySelectorAll('input, textarea, select, button, a[href]').forEach((el) => {
                if (isActive) {
                    el.removeAttribute('tabindex');
                } else {
                    el.setAttribute('tabindex', '-1');
                }
            });
        });

        state._maxStage = Math.max(state._maxStage || 0, newStage);
        state.currentStage = newStage;
        state.quickQuote.className = `qq-stage-${newStage}`;
        updateStageControls(state, newStage);

        if (newStage > previousStage && state._openedAt) {
            const fields = extractFormFields(state.form);
            if (fields.people || fields.officeType || fields.duration) {
                const elapsed = Math.round((Date.now() - state._openedAt) / 1000);
                import('./cta-analytics.js').then(m => m.trackFormProgress(
                    state._maxStage, elapsed, true, fields
                ));
            }
        }
    };

    window.clearTimeout(state.splashTimer);

    if (shouldAnimateSplash && splash) {
        splash.style.display = 'block';
        state.splashTimer = window.setTimeout(() => {
            applyStageChange();
            state.splashTimer = window.setTimeout(() => {
                splash.style.display = 'none';
            }, 4000);
        }, 1000);
        return;
    }

    if (splash) {
        splash.style.display = 'none';
    }
    applyStageChange();
}

function updateStageControls(state, stageNumber) {
    const backButton = state.form.querySelector('.qq-go-back');
    const nextButton = state.form.querySelector('.qq-next:not(.qq-submit)');
    const submitButton = state.form.querySelector('.qq-submit');
    const stage = state.form.querySelector(`.qq-stage[data-qq-stage="${stageNumber}"]`);
    const isViewingStage = state.formType === 'viewing' && stageNumber === 1;
    const hasTextInputs = Boolean(stage?.querySelector('.qq-text-input:not([hidden])'));
    const hasSelectableOptions = Boolean(stage?.querySelector('a[data-value]'));
    const hasSelection = Boolean(stage?.querySelector('.qq-selected'));
    // The viewing stage can always continue: no slot chosen means "I'm flexible"
    const allFilled = isViewingStage
        ? true
        : hasSelectableOptions
            ? hasSelection
            : (hasTextInputs ? checkRequiredFields(stage) : true);

    // PDF pitch page (stage 1) has its own buttons — hide standard nav
    const isPdfPitch = state.formType === 'pdf' && stageNumber === 1;
    backButton?.classList.toggle('qq-butt-active', stageNumber > state.startingStage && !isPdfPitch);
    if (isPdfPitch && backButton) backButton.style.display = 'none';
    if (!isPdfPitch && backButton) backButton.style.display = '';

    if (submitButton) {
        submitButton.style.display = stageNumber === state.totalStages ? 'inline-block' : 'none';
        submitButton.classList.toggle('qq-butt-active', stageNumber === state.totalStages && allFilled);
        submitButton.classList.toggle('qq-butt-visible', stageNumber === state.totalStages && !allFilled);
    }

    if (nextButton) {
        nextButton.style.display = (stageNumber === state.totalStages || isPdfPitch) ? 'none' : 'inline-block';
        nextButton.classList.remove('qq-butt-active', 'qq-butt-visible');

        if (stageNumber === state.totalStages || isPdfPitch) {
            return;
        }

        if (hasSelection || allFilled) {
            nextButton.classList.add('qq-butt-active');
        } else if (hasTextInputs || isViewingStage) {
            nextButton.classList.add('qq-butt-visible');
        }

        if (state.formType === 'viewing') {
            nextButton.textContent = (isViewingStage && !hasValidViewingSelection(state)) ? VIEWING_FLEXIBLE_LABEL : 'Continue';
        }
    }
}

function validateStage(state, stageNumber) {
    if (state.formType === 'viewing' && stageNumber === 1) {
        if (hasValidViewingSelection(state)) {
            syncViewingSelection(state, state.viewingState);
        }

        // A visitor who picks nothing goes through as flexible
        return true;
    }

    const stage = state.form.querySelector(`.qq-stage[data-qq-stage="${stageNumber}"]`);
    if (stage?.querySelector('a[data-value]')) {
        return Boolean(stage.querySelector('.qq-selected'));
    }

    return validateFields(stage);
}

function validateFields(scope) {
    if (!scope) {
        return false;
    }

    let isValid = true;
    scope.querySelectorAll('.qq-text-input').forEach((field) => {
        field.classList.remove('qq-error');
        field.querySelectorAll('.q-error-message[data-generated="true"]').forEach((message) => message.remove());
    });

    const emailInput = scope.querySelector('input[type="email"]');
    if (emailInput && !isValidEmail(emailInput.value.trim())) {
        const holder = emailInput.closest('.qq-text-input');
        holder?.classList.add('qq-error');
        isValid = false;
    }

    scope.querySelectorAll('.qq-text-input.qq-required').forEach((field) => {
        const input = field.querySelector('input[id^="qq-"], textarea[id^="qq-"]') || field.querySelector('input, textarea');
        const hasValue = Boolean(input?.value.trim());
        field.classList.toggle('had-focus', hasValue);

        if (input?.type === 'email') {
            if (!hasValue) {
                field.classList.add('qq-error');
                isValid = false;
            }
            return;
        }

        if (!hasValue) {
            field.classList.add('qq-error');
            if (!field.querySelector('.q-error-message[data-generated="true"]')) {
                const message = document.createElement('p');
                message.className = 'q-error-message';
                message.dataset.generated = 'true';
                message.textContent = 'This field is required';
                field.appendChild(message);
            }
            isValid = false;
        }
    });

    return isValid;
}

function checkRequiredFields(scope) {
    const requiredFields = [...scope.querySelectorAll('.qq-text-input.qq-required')];
    if (!requiredFields.length) {
        return Boolean(scope.querySelector('.qq-selected')) || !scope.querySelector('.qq-text-input');
    }

    const emailInput = scope.querySelector('input[type="email"]');
    if (emailInput && !isValidEmail(emailInput.value.trim())) {
        return false;
    }

    return requiredFields.every((field) => {
        const input = field.querySelector('input[id^="qq-"], textarea[id^="qq-"]') || field.querySelector('input, textarea');
        return Boolean(input?.value.trim());
    });
}

function syncShortlistPanelHeight(panel) {
    if (!panel) return;
    const count = panel.querySelectorAll('.shortlisted-property').length;
    panel.classList.toggle('shortlist-panel--full', count > 2);
}

function handleShortlistRemoval(state, entryId) {
    removeFromShortlist(entryId);
    syncShortlistUI();

    const panel = state.container.querySelector('#shortlist-panel');
    const officeIdField = state.container.querySelector('.officeID');
    const officeInterest = state.container.querySelector('#officeInterest');
    const article = panel?.querySelector(`.shortlisted-property[data-entry-id="${CSS.escape(entryId)}"]`);

    const finishRemoval = () => {
        const items = getShortlistItems();

        if (!items.length) {
            closeDialog(state.dialog);
            return;
        }

        if (panel) {
            panel.innerHTML = buildShortlistPanel(items);
            attachShortlistImageFallbacks(panel);
            syncShortlistPanelHeight(panel);
        }
        if (officeIdField) {
            officeIdField.value = items.map((item) => item.entryId).join(',');
        }
        if (officeInterest) {
            officeInterest.value = items.map((item) => item.title).join(', ');
        }

        updateShortlistMeta(state, items);

        if (items.length < 2) {
            toggleShortlistPanel(state, false);
        }
    };

    if (article) {
        article.style.overflow = 'hidden';
        article.style.transition = 'max-height 0.3s ease, opacity 0.25s ease';
        article.style.maxHeight = article.scrollHeight + 'px';
        // Force reflow so the starting max-height is applied before animating
        article.offsetHeight;
        article.style.maxHeight = '0';
        article.style.opacity = '0';
        article.addEventListener('transitionend', finishRemoval, { once: true });
    } else {
        finishRemoval();
    }
}

function updateViewingMessage(state, value) {
    const preferredText = state.form.querySelector('.preferred-viewing-declared');
    const questionField = state.form.querySelector('#qq-question');
    const questionHolder = questionField?.closest('.qq-text-input');

    if (!value) {
        if (preferredText) {
            const viewing = state.viewingState;
            const waitingForDate = viewing && viewing.selectedTime && !viewing.dayChosen && viewing.timeTapped;
            preferredText.textContent = waitingForDate
                ? `Tap a date to confirm ${viewing.selectedTime}`
                : 'Flexible on date and time';
            preferredText.classList.add('is-hint');
        }

        const resetButton = state.form.querySelector('[data-viewing-reset]');
        if (resetButton) {
            resetButton.hidden = true;
        }

        if (questionField) {
            questionField.value = `${VIEWING_PREFIX}${VIEWING_FLEXIBLE_MESSAGE} * `;
            questionHolder?.classList.add('had-focus');
        }

        updateStageControls(state, state.currentStage);
        return;
    }

    const selected = new Date(value);
    if (Number.isNaN(selected.getTime())) {
        return;
    }

    const resetButton = state.form.querySelector('[data-viewing-reset]');
    if (resetButton) {
        resetButton.hidden = false;
    }

    const formatted = formatViewingDate(selected);

    if (preferredText) {
        preferredText.textContent = formatViewingDateShort(selected);
        preferredText.classList.remove('is-hint');
    }

    if (questionField) {
        questionField.value = `${VIEWING_PREFIX}${formatted} * `;
        questionHolder?.classList.add('had-focus');
    }

    updateStageControls(state, state.currentStage);
}

function combinePhone(form) {
    const countryCode = form.querySelector('#qq-country-code-select2-flags')?.value || '';
    const phoneNumber = form.querySelector('#qq-phone')?.value.trim() || '';
    const combined = [countryCode, phoneNumber].filter(Boolean).join(' ');
    const hiddenInput = form.querySelector('#telephone-combined');
    if (hiddenInput) {
        hiddenInput.value = combined;
    }
}

function applyCountryFlag(select, countryIso) {
    const flagEl = select.parentElement?.querySelector('.country-select-flag-inline use');
    if (flagEl) {
        flagEl.setAttribute('href', `${PHONE_FLAGS_SPRITE}#cig--flags--${countryIso}`);
    }
}

function getSelectedCountryIso(select, fallbackIso = '') {
    return select.dataset.selectedIso || select.selectedOptions[0]?.dataset.countryCode || fallbackIso;
}

function enhanceCountrySelect(select, initialIso) {
    const holder = select.closest('.select2-country-flags');
    if (!holder) {
        applyCountryFlag(select, initialIso);
        return;
    }

    holder.querySelector('.country-select-trigger')?.remove();
    holder.querySelector('.country-select-menu')?.remove();

    // Remove previous document click handler if this picker was already enhanced
    if (holder._countryPickerCloseHandler) {
        document.removeEventListener('click', holder._countryPickerCloseHandler);
    }

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'country-select-trigger';

    const menu = document.createElement('div');
    menu.className = 'country-select-menu';
    menu.hidden = true;

    // Scrollable options list
    const optionsList = document.createElement('div');
    optionsList.className = 'country-select-options';

    [...select.options].forEach((option) => {
        // Insert separator before marked options
        if (option.dataset.separator === 'true') {
            const sep = document.createElement('div');
            sep.className = 'country-select-separator';
            optionsList.appendChild(sep);
        }

        const iso = option.dataset.countryCode || '';
        const isTopPick = option.dataset.topPick === 'true';
        const rawLabel = option.textContent.trim();
        const countryName = rawLabel.replace(/\s*\([^)]*\)\s*$/, '');
        const tags = option.dataset.tags || '';
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'country-select-option';
        item.dataset.value = option.value;
        item.dataset.iso = iso;
        item.dataset.searchText = `${countryName} ${option.value} ${tags}`.toLowerCase();
        if (isTopPick) item.style.fontWeight = '600';
        item.innerHTML = `
            <svg class="country-select-flag" aria-hidden="true"><use href="${PHONE_FLAGS_SPRITE}#cig--flags--${iso}"></use></svg>
            <span class="country-select-label">${escapeHtml(countryName)}</span>
            <span class="country-select-dial">${escapeHtml(option.value)}</span>
        `;
        item.addEventListener('click', () => {
            select.value = option.value;
            select.dataset.selectedIso = iso || initialIso;
            applyCountryFlag(select, iso || initialIso);
            updateCountrySelectTrigger(trigger, option.value, iso || initialIso);
            closeMenu();
            select.dispatchEvent(new Event('change', { bubbles: true }));
            holder.closest('.qq-telephone-field-holder')?.querySelector('#qq-phone')?.focus();
        });
        optionsList.appendChild(item);
    });

    menu.appendChild(optionsList);

    const closeMenu = ({ resetSearch = true, returnFocus = false } = {}) => {
        holder.classList.remove('is-open');
        menu.hidden = true;
        parentStage?.classList.remove('country-menu-open');

        if (resetSearch) {
            search.reset();
        } else {
            search.clearActive();
        }

        if (returnFocus) {
            trigger.focus();
        }
    };

    const getSelectedOptionButton = () => {
        const selectedIso = getSelectedCountryIso(select, initialIso);
        return [...optionsList.querySelectorAll('.country-select-option')].find(item => item.dataset.iso === selectedIso)
            || [...optionsList.querySelectorAll('.country-select-option')].find(item => item.dataset.value === select.value)
            || null;
    };

    const search = addDropdownSearch(menu, {
        placeholder: 'Search countries...',
        optionSelector: '.country-select-option',
        separatorSelector: '.country-select-separator',
        getSearchText: item => item.dataset.searchText || '',
        onClose: () => closeMenu({ returnFocus: true }),
        wrapClassName: 'country-select-search dropdown-search',
        inputClassName: 'country-select-search-input dropdown-search-input',
    });

    function positionMenu() {
        const triggerRect = trigger.getBoundingClientRect();
        // <dialog> in top layer creates a containing block for position:fixed children,
        // so we need coordinates relative to the dialog, not the viewport
        const dialog = holder.closest('dialog');
        const dialogRect = dialog?.getBoundingClientRect();
        const offsetTop = dialogRect ? dialogRect.top : 0;
        const offsetLeft = dialogRect ? dialogRect.left : 0;

        const menuWidth = menu.offsetWidth || 300;
        const menuHeight = menu.offsetHeight || 320;
        const spaceBelow = window.innerHeight - triggerRect.bottom - 8;
        const openUpward = spaceBelow < menuHeight && triggerRect.top > spaceBelow;

        if (openUpward) {
            menu.style.top = 'auto';
            menu.style.bottom = `${(dialogRect ? dialogRect.bottom : window.innerHeight) - triggerRect.top + 4}px`;
        } else {
            menu.style.top = `${triggerRect.bottom - offsetTop + 4}px`;
            menu.style.bottom = 'auto';
        }
        // Clamp left so menu stays within viewport
        const maxLeft = window.innerWidth - menuWidth - 8;
        const idealLeft = triggerRect.left - offsetLeft;
        menu.style.left = `${Math.max(8 - offsetLeft, Math.min(idealLeft, maxLeft - offsetLeft))}px`;
    }

    const parentStage = holder.closest('.qq-stage');

    trigger.addEventListener('click', (event) => {
        event.preventDefault();
        const nextOpen = menu.hidden;
        // Close any other open dropdowns
        document.querySelectorAll('.select2-country-flags.is-open').forEach((openHolder) => {
            if (openHolder !== holder) {
                openHolder.classList.remove('is-open');
                openHolder.closest('.qq-stage')?.classList.remove('country-menu-open');
                const openMenu = openHolder.querySelector('.country-select-menu');
                if (openMenu) openMenu.hidden = true;
            }
        });
        if (!nextOpen) {
            closeMenu();
            return;
        }

        holder.classList.add('is-open');
        menu.hidden = false;
        parentStage?.classList.add('country-menu-open');
        search.reset();
        search.setActiveOption(getSelectedOptionButton() || search.getVisibleOptions()[0] || null);

        // Position after browser has laid out the menu
        requestAnimationFrame(() => {
            positionMenu();
            search.focus();
        });
    });

    trigger.addEventListener('keydown', (event) => {
        if (!['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
            return;
        }

        event.preventDefault();

        if (menu.hidden) {
            holder.classList.add('is-open');
            menu.hidden = false;
            parentStage?.classList.add('country-menu-open');
            search.reset();
            search.setActiveOption(getSelectedOptionButton() || search.getVisibleOptions()[0] || null);

            if (event.key === 'ArrowUp') {
                const visibleOptions = search.getVisibleOptions();
                search.setActiveOption(visibleOptions[visibleOptions.length - 1] || null);
            }

            requestAnimationFrame(() => {
                positionMenu();
                search.focus();
            });
            return;
        }

        closeMenu();
    });

    holder._countryPickerCloseHandler = (event) => {
        if (!holder.contains(event.target) && !menu.contains(event.target)) {
            closeMenu();
        }
    };
    document.addEventListener('click', holder._countryPickerCloseHandler);

    holder.appendChild(trigger);
    holder.appendChild(menu);

    applyCountryFlag(select, initialIso);
    updateCountrySelectTrigger(trigger, select.value, initialIso);
}

function updateCountrySelectTrigger(trigger, value, iso) {
    trigger.innerHTML = `
        <svg class="country-select-flag" aria-hidden="true"><use href="${PHONE_FLAGS_SPRITE}#cig--flags--${iso}"></use></svg>
        <span class="country-select-dial">${escapeHtml(value)}</span>
    `;
    syncPhonePadding(trigger);
}

function syncPhonePadding(trigger) {
    const holder = trigger.closest('.qq-telephone-field-holder');
    if (!holder) return;
    const phoneInput = holder.querySelector('#qq-phone');
    const flagsContainer = holder.querySelector('.select2-country-flags');
    if (!phoneInput || !flagsContainer) return;
    requestAnimationFrame(() => {
        phoneInput.style.paddingLeft = (flagsContainer.offsetWidth + 14) + 'px';
    });
}

function buildLegacyMarkup(type, context) {
    const currentOffice = context.currentOffice;
    const heading = context.officeNames.length > 1 && type === 'quote'
        ? `${FORM_HEADINGS[type]} (${context.officeNames.length} offices)`
        : FORM_HEADINGS[type];

    return `
        <div class="get-details get-details--dialog" data-form-type="${escapeHtml(type)}" id="form-${escapeHtml(type)}">
            <div id="enquiry-wrapper"${type === 'shortlist' && context.officeIds.length ? ' class="with-shortlist"' : ''}>
                ${type === 'shortlist' ? `
                    <button type="button" class="shortlist-edit-toggle" aria-controls="shortlist-panel" aria-expanded="false"${context.officeIds.length < 2 ? ' hidden' : ''}>
                        <span class="shortlist-edit-label">Edit</span>
                        <span class="shortlist-edit-count">${context.officeIds.length || ''}</span>
                    </button>
                ` : ''}
                <a class="close-form" href="#">Close form</a>
                <div id="shortlist-panel"${type === 'shortlist' ? '' : ' hidden'}>
                    ${buildShortlistPanel(type === 'shortlist' ? getShortlistItems() : [])}
                </div>
                <form action="${window.FRED_SEND_URL}" method="post">
                    <div id="enquiry-heading">
                        ${type === 'callback' ? `
                            <div class="enquiry-thumb-holder enquiry-thumb-holder--icon">
                                <svg class="enquiry-thumb-icon" aria-hidden="true"><use href="/img/sprite-general.svg?v=20260318#icon-callback"></use></svg>
                            </div>
                        ` : type === 'question' && document.body.id !== 'details' ? `
                            <div class="enquiry-thumb-holder enquiry-thumb-holder--icon">
                                <svg class="enquiry-thumb-icon" aria-hidden="true"><use href="/img/sprite-general.svg?v=20260318#icon-edit"></use></svg>
                            </div>
                        ` : context.locationLabel ? `
                            <div class="enquiry-thumb-holder enquiry-thumb-holder--icon">
                                <svg class="enquiry-thumb-icon" aria-hidden="true"><use href="/img/sprite-general.svg?v=20260318#icon-quote"></use></svg>
                            </div>
                        ` : context.thumbImage ? `
                            <div class="enquiry-thumb-holder">
                                <img src="${escapeHtml(context.thumbImage)}" alt="${escapeHtml(context.subtitle || currentOffice.title)}" class="enquiry-thumb-image">
                            </div>
                        ` : ''}
                        <div class="enquiry-heading-copy">
                            <h3>${escapeHtml(heading)}</h3>
                            ${buildHeadingSubtitle(type, context, currentOffice)}
                        </div>
                    </div>

                    <div id="main-form">
                        <div class="formFields">
                            <input type="hidden" value="${window.EE_CSRF_TOKEN || ''}" name="csrf_token" id="csrf_token">
                            <input type="hidden" value="${escapeHtml(context.sourceUrl)}" name="url" id="url">
                            <input type="hidden" value="default" name="language" id="language">
                            <input type="hidden" name="browser" id="browser" value="${escapeHtml(navigator.userAgent)}">
                            <input type="hidden" name="session_hash" id="session_hash" value="">
                            <input type="hidden" name="viewport" id="viewport" value="${window.innerWidth}x${window.innerHeight}">
                            <input type="hidden" name="adword_referral" id="adword_referral" value="false">
                            <input type="hidden" name="cta_source" id="cta_source" value="${escapeHtml(context.category || '')}">
                            <input type="hidden" name="formType" class="formType" value="${escapeHtml(type)}">
                            <input type="hidden" name="officeID" class="officeID" data-starting-value="${escapeHtml(context.officeIds.join(','))}" value="${escapeHtml(context.officeIds.join(','))}">
                            <input type="hidden" name="officeInterest" id="officeInterest" value="${escapeHtml(context.officeInterest)}" class="hadInput">
                            <input type="hidden" name="country" id="country" value="${escapeHtml(context.country)}">
                            <input type="hidden" name="city" id="city" value="${escapeHtml(context.city)}">
                            <input type="hidden" name="desks" id="desks" value="" autocomplete="off">
                            <input type="hidden" name="term" id="term" value="" autocomplete="off">
                            <input type="hidden" name="telephone" id="telephone-combined" value="">
                            <input type="hidden" name="reference" id="reference" value="${escapeHtml(context.reference)}">
                            <input type="hidden" name="pu_date" value="${window.EE_ENTRY_DATE || ''}">
                            <input type="hidden" name="ed_date" value="${window.EE_EDIT_DATE || ''}">

                            <div id="enquiry" class="enquiry-honeypot">
                                <label>Enquiry message</label>
                                <textarea name="enquiry" class="message"></textarea>
                                <label for="website_url">This field is intended to catch out spammers - please leave it blank.</label>
                                <input type="text" autocomplete="off" id="website_url" name="website_url">
                            </div>
                        </div>
                    </div>

                    <div class="form-submitting">
                        <p>Your enquiry is being processed<br>Please wait...</p>
                    </div>

                    <div id="quick-quote">
                        <div class="qq-gen-quote-splash">
                            <div class="qq-gen-quote-splash-copy">
                                <p class="qq-gen-quote-splash-intro">Finding the best prices for</p>
                                ${context.headerAddress
                                    ? `<p class="qq-gen-quote-splash-subject qq-gen-quote-splash-subject--split"><span class="qq-gen-quote-splash-primary">${escapeHtml(context.headerAddress.primary)}</span><span class="qq-gen-quote-splash-secondary">${escapeHtml(context.headerAddress.secondary)}</span></p>`
                                    : `<p class="qq-gen-quote-splash-subject">${escapeHtml(context.subtitle || 'Your workspace request')}</p>`}
                            </div>
                        </div>
                        <div>
                            <div class="qq-progress">
                                <div><div class="qq-bar-progess"></div></div>
                                <p class="qq-percent-progess"><span></span></p>
                            </div>

                            ${buildStageOneMarkup(type, context)}

                            <div class="qq-stage" data-qq-stage="2" data-target-field="desks">
                                <h4>How many people do you need workspace for?</h4>
                                <div class="qq-size-values qq-panelled">
                                    <div>
                                        ${buildOptionLinks([
                                            ['1', '1 person'],
                                            ['2', '2 people'],
                                            ['3-4', '3-4 people'],
                                            ['5-9', '5-9 people'],
                                            ['10-19', '10-19 people'],
                                            ['20-49', '20-49 people'],
                                            ['50-99', '50-99 people'],
                                            ['100+', '100+ people'],
                                        ])}
                                    </div>
                                </div>
                            </div>

                            <div class="qq-stage" data-qq-stage="3" data-target-field="office-type">
                                <h4>What sort of workspace do you need?</h4>
                                <div class="qq-type-values qq-panelled">
                                    <div>
                                        ${buildOfficeTypeLinks()}
                                    </div>
                                </div>
                            </div>

                            <div class="qq-stage" data-qq-stage="4" data-target-field="term">
                                <h4>How long would you need it for?</h4>
                                <div class="qq-term-values qq-panelled">
                                    <div>
                                        ${buildOptionLinks([
                                            ['Less than 3 months', 'less than 3 months'],
                                            ['3-6 months', '3-6 months'],
                                            ['6-12 months', '6-12 months'],
                                            ['12 months+', 'more than 1 year'],
                                            ['Not sure', 'not sure'],
                                        ])}
                                    </div>
                                </div>
                            </div>

                            <div class="qq-stage qq-stage--contact" data-qq-stage="5" data-target-field="contact">
                                <div><h4>Great! Just a few more details...</h4></div>
                                <div class="qq-contact">
                                    <div>
                                        <div data-target-div="pop-7" class="qq-text-input qq-required has-info">
                                            <input type="email" value="" id="qq-email" name="email" data-target-field="email" ${autocompleteAttribute('email')}>
                                            <label for="qq-email">Email</label>
                                            <button
                                                type="button"
                                                class="info-trigger pop-label"
                                                data-tooltip="One of our specialist advisors or a relevant office operator will contact you via email and/or telephone with the latest rates and availability."
                                                data-tooltip-position="right"
                                                aria-label="Why we need your email"
                                            >
                                                <svg class="info-trigger-icon" aria-hidden="true"><use href="/img/sprite-general.svg?v=20260318#icon-info"></use></svg>
                                            </button>
                                            <p class="q-error-message">Please ensure you have entered a valid email address</p>
                                        </div>

                                        <div class="qq-text-input qq-required qq-telephone-field-holder">
                                            <div class="select2-country-flags select2-holder">
                                                <select name="qq-country-code-select2-flags" id="qq-country-code-select2-flags" class="sel2-telcode flag-select" autocomplete="off">
                                                    ${buildCountryOptions()}
                                                </select>
                                            </div>
                                            <input type="tel" value="" id="qq-phone" name="qq-phone" data-target-field="telephone" ${autocompleteAttribute('tel')}>
                                            <label for="qq-phone">Telephone</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="qq-stage qq-stage--contact" data-qq-stage="6" data-target-field="name">
                                <div><h4>Almost done!</h4></div>
                                <div class="qq-contact">
                                    <div>
                                        <div class="qq-text-input qq-required">
                                            <input type="text" value="" id="qq-name" name="name" data-target-field="name" ${autocompleteAttribute('given-name')}>
                                            <label for="qq-name">First name</label>
                                        </div>

                                        <div class="qq-text-input qq-required">
                                            <input type="text" value="" id="qq-surname" name="surname" data-target-field="surname" ${autocompleteAttribute('family-name')}>
                                            <label for="qq-surname">Last name</label>
                                        </div>

                                        <div class="qq-text-input qq-required">
                                            <input type="text" value="" id="qq-company" name="company" data-target-field="company" ${autocompleteAttribute('organization')}>
                                            <label for="qq-company">Company name</label>
                                        </div>

                                        <p class="disclaimer">By submitting this application you agree that we may use your information and accept our <a href="/terms-of-service" target="_blank" rel="noreferrer">terms of service</a> and <a href="/privacy" target="_blank" rel="noreferrer">privacy policy</a>.</p>

                                        <a class="qq-submit qq-next" href="#">${SUBMIT_LABELS[type] || 'SUBMIT'}</a>
                                    </div>
                                </div>
                            </div>

                            <a class="qq-go-back" href="#">Go back</a>
                            <a class="qq-next" href="#">Continue</a>
                        </div>
                    </div>
                </form>
                ${buildAdminTestFillButton()}
            </div>
        </div>
    `;
}

/**
 * The siblify_admin account tests the forms by hand, so browser autofill is kept
 * out of its way; everyone else gets proper hints so phones can fill in one tap.
 */
function isAdminUser() {
    return Boolean(window.NON_ADMIN_CONFIG && window.NON_ADMIN_CONFIG.isAdmin);
}

function autocompleteAttribute(kind) {
    return `autocomplete="${isAdminUser() ? 'off' : kind}"`;
}

function buildAdminTestFillButton() {
    if (!isAdminUser()) {
        return '';
    }
    return '<button type="button" class="admin-test-fill" data-admin-test-fill>Admin: fill with test values</button>';
}

/**
 * Admin only: fills every field with test values (name "Test Test" marks the
 * submission as a test for FredSend) and jumps to the final stage.
 */
function fillFormWithTestValues(state) {
    const form = state.form;
    const setValue = (selector, value) => {
        const input = form.querySelector(selector);
        if (!input) {
            return;
        }
        input.value = value;
        input.dispatchEvent(new Event('input', { bubbles: true }));
    };

    // Legacy single-page callback form (chatbot); the dialog callback uses the staged form below
    if (form.querySelector('#qq-name_callback')) {
        setValue('#qq-question_callback', 'Test enquiry - please ignore');
        setValue('#qq-name_callback', 'Test');
        setValue('#qq-surname_callback', 'Test');
        setValue('#qq-company_callback', 'Test Company');
        setValue('#qq-email_callback', 'test@example.com');
        setValue('#qq-telephone_callback', '+44 7700 900123');
        setValue('#qq-size_callback', '3-4 people');
        setValue('#qq-term_callback', '6-12 months');
        return;
    }

    // Option stages: take the first real choice on each
    form.querySelectorAll('.qq-stage').forEach((stage) => {
        const option = [...stage.querySelectorAll('a[data-value]')].find((link) => link.dataset.value) || stage.querySelector('a[data-value]');
        if (option) {
            applyOptionSelection(state, option);
        }
    });

    if (state.formType !== 'viewing') {
        setValue('#qq-question', 'Test enquiry - please ignore');
    }
    setValue('#qq-email', 'test@example.com');
    setValue('#qq-phone', '7700 900123');
    setValue('#qq-name', 'Test');
    setValue('#qq-surname', 'Test');
    setValue('#qq-company', 'Test Company');

    activateStage(state, state.totalStages, { animateSplash: false });
    updateStageControls(state, state.currentStage);
}

function bindAdminTestFill(state) {
    const button = state.container?.querySelector('[data-admin-test-fill]');
    if (!button) {
        return;
    }
    button.addEventListener('click', (event) => {
        event.preventDefault();
        fillFormWithTestValues(state);
    });
}

function buildHeadingSubtitle(type, context, currentOffice) {
    if (type === 'question' && document.body.id !== 'details') {
        return '<h4 class="subtitle-quote">We\'d love to hear from you</h4>';
    }

    const fullTitle = context.subtitle || currentOffice.title || '';
    if (context.headerAddress) {
        return `
            <h4 class="subtitle-quote subtitle-quote--split" title="${escapeHtml(fullTitle)}">
                <span class="subtitle-primary">${escapeHtml(context.headerAddress.primary)}</span>
                <span class="subtitle-secondary">${escapeHtml(context.headerAddress.secondary)}</span>
            </h4>
        `;
    }

    return `<h4 class="subtitle-quote">${escapeHtml(fullTitle)}</h4>`;
}

/**
 * The two title lines the details template renders server-side, so the dialog
 * header always matches the page heading
 */
function getDetailsPageTitleLines() {
    const primary = document.querySelector('.office-title .office-title-primary')?.textContent?.trim() || '';
    const secondary = document.querySelector('.office-title .office-title-secondary')?.textContent?.trim() || '';
    return primary && secondary ? { primary, secondary } : null;
}

/**
 * City and country as printed under the office title on details pages ("Lagos, Nigeria")
 */
function getDetailsPageAddress() {
    const text = document.querySelector('.office-address')?.textContent?.trim() || '';
    const index = text.lastIndexOf(',');
    if (!text) {
        return { city: '', country: '' };
    }
    if (index < 0) {
        return { city: text, country: '' };
    }
    return { city: text.slice(0, index).trim(), country: text.slice(index + 1).trim() };
}

function buildCallbackMarkup(context) {
    return `
        <div class="get-details get-details--dialog" data-form-type="callback" id="form-callback">
            <div id="enquiry-wrapper" class="enquiry-wrapper--callback">
                <a class="close-form" href="#">Close form</a>
                <div id="callback">
                    <div id="enquiry-heading">
                        <h3>Request Callback</h3>
                        <h4 class="subtitle-quote">${escapeHtml(
                            document.body.id !== 'details' && !context.locationLabel
                                ? 'We\'ll get back to you shortly'
                                : (context.subtitle || context.currentOffice.title)
                        )}</h4>
                    </div>

                    <form action="${window.FRED_SEND_URL}" method="post">
                        <input type="hidden" value="${window.EE_CSRF_TOKEN || ''}" name="csrf_token">
                        <input type="hidden" name="session_hash" id="session_hash" value="">
                        <input type="hidden" value="callback" name="formType" id="callback-field">
                        <input type="hidden" value="${escapeHtml(context.sourceUrl)}" name="url" id="url">
                        <div class="qq-stage qq-active-panel">
                            <p class="callback-intro">The local consultant for <span>${escapeHtml(context.currentOffice.location || context.currentOffice.title)}</span> will contact you within 24 hours.</p>

                            <div class="qq-text-input">
                                <textarea id="qq-question_callback" name="question" rows="3" data-target-field="question"></textarea>
                                <label for="qq-question_callback">How can we help?</label>
                            </div>

                            <div class="qq-text-input qq-required">
                                <input type="text" value="" id="qq-name_callback" name="name" data-target-field="name" ${autocompleteAttribute('given-name')}>
                                <label for="qq-name_callback">First name</label>
                            </div>

                            <div class="qq-text-input qq-required">
                                <input type="text" value="" id="qq-surname_callback" name="surname" data-target-field="surname" ${autocompleteAttribute('family-name')}>
                                <label for="qq-surname_callback">Last name</label>
                            </div>

                            <div class="qq-text-input qq-required">
                                <input type="text" value="" id="qq-company_callback" name="company" data-target-field="company" ${autocompleteAttribute('organization')}>
                                <label for="qq-company_callback">Company name</label>
                            </div>

                            <div class="qq-text-input qq-required">
                                <input type="email" value="" id="qq-email_callback" name="email" data-target-field="email" ${autocompleteAttribute('email')}>
                                <label for="qq-email_callback">Email</label>
                                <p class="q-error-message">Please enter a valid email address</p>
                            </div>

                            <div class="qq-text-input qq-required">
                                <input type="text" value="" id="qq-telephone_callback" name="telephone" data-target-field="telephone" ${autocompleteAttribute('tel')}>
                                <label for="qq-telephone_callback">(+00) Telephone</label>
                            </div>

                            <div class="qq-text-input qq-required">
                                <input type="text" value="" id="qq-size_callback" name="desks" data-target-field="desks">
                                <label for="qq-size_callback">Office size</label>
                            </div>

                            <div class="qq-text-input qq-required">
                                <input type="text" value="" id="qq-term_callback" name="term" data-target-field="term">
                                <label for="qq-term_callback">How long do you need it for?</label>
                            </div>

                            <div class="enquiry-honeypot">
                                <label for="callback_website_url">This field is intended to catch out spammers - please leave it blank.</label>
                                <input type="text" autocomplete="off" id="callback_website_url" name="website_url">
                            </div>
                        </div>

                        <div class="form-submitting">
                            <p>Your enquiry is being processed<br>Please wait...</p>
                        </div>

                        <a class="qq-submit qq-next qq-butt-active" href="#">REQUEST CALLBACK</a>
                        <p class="disclaimer">By submitting this application and requesting our services, you agree that we may use your information and accept the conditions as set out in our <a href="/terms-of-service" target="_blank" rel="noreferrer">terms of service</a> and <a href="/privacy" target="_blank" rel="noreferrer">privacy policy</a>.</p>
                    </form>
                    ${buildAdminTestFillButton()}
                </div>
            </div>
        </div>
    `;
}

function buildShortlistPanel(items) {
    const shortlistItems = items.length
        ? items.map((item) => `
            <article class="shortlisted-property" data-entry-id="${escapeHtml(item.entryId)}">
                <div class="shortlisted-property-titlebar" title="${escapeHtml(item.title)}">${escapeHtml(splitOfficeAddress(item.title)?.primary || item.title)}</div>
                <div class="shortlisted-property-media">
                    <img src="${escapeHtml(item.image || '/img/images-coming-soon.svg')}" alt="${escapeHtml(item.title)}" class="shortlisted-property-photo${item.image ? '' : ' shortlisted-property-photo--placeholder'}">
                    <div class="shortlisted-property-actions">
                        <a class="shortlisted-property-action shortlisted-property-remove-link" href="#" data-entry-id="${escapeHtml(item.entryId)}">Remove property</a>
                        <a class="shortlisted-property-action shortlisted-property-view" href="${escapeHtml(item.href || '#')}">View property</a>
                    </div>
                    <a class="shortlisted-property-remove" href="#" aria-label="Remove from shortlist" data-entry-id="${escapeHtml(item.entryId)}">Remove</a>
                </div>
            </article>
        `).join('')
        : '<p class="shortlist-empty">Your shortlist is currently empty.</p>';

    return `
        <div class="related-offices${items.length ? ' sl-visible' : ''}">
            <span class="shortlist-count-badge">${items.length || ''}</span>
            <h3>Your shortlist</h3>
            <button type="button" class="shortlist-panel-close dialog-close" aria-label="Close">&times;</button>
        </div>
        <div class="shortlist-panel-body">
            ${shortlistItems}
        </div>
    `;
}

function attachShortlistImageFallbacks(container) {
    if (!container) return;
    container.querySelectorAll('.shortlisted-property-photo').forEach((img) => {
        img.addEventListener('error', function handleImgError() {
            this.removeEventListener('error', handleImgError);
            this.src = '/img/images-coming-soon.svg';
            this.classList.add('shortlisted-property-photo--placeholder');
        });
    });
}

function buildOptionLinks(options) {
    return options
        .map(([label, value]) => `<a href="#" data-value="${escapeHtml(value)}">${escapeHtml(label)}</a>`)
        .join('');
}

function buildOfficeTypeLinks() {
    return [
        ['co-working', 'Regarding Co-working desks', 'office-type-coworking.webp', 'Co-working desks'],
        ['private', 'Regarding Private Office', 'office-type-private.webp', 'Private office'],
        ['virtual', 'Regarding Virtual Office', 'office-type-virtual.webp', 'Virtual office'],
        ['not-sure', '', 'office-type-not-sure.webp', 'Not sure'],
    ]
        .map(([className, value, image, label]) => `
            <a class="${className}" href="#" data-value="${escapeHtml(value)}" data-label="${escapeHtml(label)}">
                <img src="/img/${escapeHtml(image)}" alt="${escapeHtml(label)}">
                <span>${escapeHtml(label)}</span>
            </a>
        `)
        .join('');
}

function buildStageOneMarkup(type, context = {}) {
    if (type === 'pdf') {
        return `
            <div class="qq-stage qq-stage--pdf-pitch" data-qq-stage="1">
                <div class="pdf-pitch">
                    <svg style="width:48px;height:48px;margin:0 auto 12px;display:block;" aria-hidden="true"><use href="/img/sprite-general.svg#icon-pdf"></use></svg>
                    <h4 style="margin:0 0 8px; text-align:center;">Get Full Pricing & Availability</h4>
                    <p style="text-align:center; color:#555; font-size:14px; margin:0 0 20px; line-height:1.5;">Submit your details and our advisory team will send you full details, current pricing and live availability for this office.</p>
                    <div style="display:flex; flex-direction:column; gap:10px; align-items:center;">
                        <button type="button" class="pdf-proceed-btn btn btn-cta-primary" style="width:100%; max-width:320px; padding:14px 24px; font-size:15px;">Get full details & download</button>
                        <button type="button" class="pdf-skip-btn" style="background:none; border:none; color:#64748b; font-size:13px; cursor:pointer; padding:8px; margin-top:15px; text-decoration:underline;">No thanks, just download the PDF</button>
                    </div>
                </div>
            </div>
        `;
    }

    if (type === 'viewing') {
        const viewingCity = String(context?.viewingCity || '').trim();
        const timezoneNote = viewingCity ? `${escapeHtml(viewingCity)} local time` : 'Local time at the office';
        return `
            <div class="qq-stage qq-stage--viewing" data-qq-stage="1" data-target-field="question">
                <h4>When would you like to visit? <small class="viewing-heading-note">(subject to arrangement with the centre)</small></h4>
                <div class="qq-question" hidden>
                    <div>
                        <div class="qq-text-input qq-required" hidden>
                            <textarea id="qq-question" name="question" data-target-field="question"></textarea>
                            <label for="qq-question">Your message</label>
                        </div>
                    </div>
                </div>
                <div class="select-viewing-date">
                    <div class="viewing-picker" data-viewing-picker>
                        <div class="viewing-section viewing-section--date">
                            <div class="viewing-picker-toolbar">
                                <button type="button" class="viewing-picker-nav" data-viewing-prev aria-label="Previous month">${VIEWING_CHEVRON_LEFT}</button>
                                <h5 class="viewing-section-label viewing-section-label--month"><span class="viewing-date-prompt" data-viewing-date-prompt hidden>Choose a date:</span> <span class="viewing-picker-month" data-viewing-month aria-live="polite"></span></h5>
                                <button type="button" class="viewing-picker-nav" data-viewing-next aria-label="Next month">${VIEWING_CHEVRON_RIGHT}</button>
                            </div>
                            <div class="viewing-calendar">
                                <div class="viewing-weekdays">
                                    <span>Sun</span>
                                    <span>Mon</span>
                                    <span>Tue</span>
                                    <span>Wed</span>
                                    <span>Thu</span>
                                    <span>Fri</span>
                                    <span>Sat</span>
                                </div>
                                <div class="viewing-days" data-viewing-days></div>
                            </div>
                        </div>
                        <div class="viewing-section viewing-section--time">
                            <h5 class="viewing-section-label">Choose a time<span class="viewing-section-note">${timezoneNote}</span></h5>
                            <div class="viewing-times-wrap is-at-start">
                                <button type="button" class="viewing-time-scroll viewing-time-scroll--prev" data-viewing-time-scroll="-1" aria-label="Scroll to earlier times">${VIEWING_CHEVRON_LEFT}</button>
                                <div class="viewing-times" data-viewing-times></div>
                                <button type="button" class="viewing-time-scroll viewing-time-scroll--next" data-viewing-time-scroll="1" aria-label="Scroll to later times">${VIEWING_CHEVRON_RIGHT}</button>
                            </div>
                        </div>
                    </div>
                    <input type="hidden" id="datetimepicker">
                    <p class="preferred-viewing-declared"></p>
                    <button type="button" class="viewing-reset" data-viewing-reset hidden>Not sure? I'm flexible</button>
                </div>
            </div>
        `;
    }

    return `
        <div class="qq-stage" data-qq-stage="1" data-target-field="question">
            <h4>How can we help?</h4>
            <div class="qq-question">
                <div>
                    <div class="qq-text-input qq-required">
                        <textarea id="qq-question" name="question" data-target-field="question"></textarea>
                        <label for="qq-question">Your message</label>
                    </div>
                </div>
            </div>
            <div class="select-viewing-date" hidden>
                <h4>Select Preferred Date & Time</h4>
                <input type="hidden" id="datetimepicker">
                <p class="preferred-viewing-declared"></p>
            </div>
        </div>
    `;
}

function getOrderedCountryOptions() {
    const region = document.querySelector('.site-title-region')?.textContent?.trim().toLowerCase() || '';
    const regionPriority = REGION_PRIORITY[region] || [];

    // Top picks: user's IP country + entry's country (bold, separated)
    // Use IP-detected country (actual location) not page-context country
    let ipIso = '';
    try {
        ipIso = (localStorage.getItem('newofficeplease_ip_country') || '').trim().toLowerCase();
    } catch(e) {}
    const entryIso = (document.body.getAttribute('data-entry-country') || '').trim().toLowerCase();
    const topIsos = [];
    if (entryIso) topIsos.push(entryIso);
    if (ipIso && ipIso !== entryIso) topIsos.push(ipIso);

    const topPicks = [];
    const priority = [];
    const rest = [];
    const usedIsos = new Set();

    // Build top picks first
    topIsos.forEach(iso => {
        if (usedIsos.has(iso)) return;
        const option = COUNTRY_OPTIONS.find(o => o.iso === iso);
        if (option) {
            topPicks.push({ ...option, isTopPick: true });
            usedIsos.add(iso);
        }
    });

    // Then region + always-on priority
    [...regionPriority, ...ALWAYS_PRIORITY].forEach(iso => {
        if (usedIsos.has(iso)) return;
        const option = COUNTRY_OPTIONS.find(o => o.iso === iso);
        if (option) {
            priority.push(option);
            usedIsos.add(iso);
        }
    });

    // The rest alphabetically
    COUNTRY_OPTIONS.forEach(option => {
        if (!usedIsos.has(option.iso)) {
            rest.push(option);
            usedIsos.add(option.iso);
        }
    });

    return { topPicks, priority, rest };
}

function buildCountryOptions() {
    const { topPicks, priority, rest } = getOrderedCountryOptions();
    const all = [...topPicks, ...priority, ...rest];
    return all
        .map((option, i) => {
            // Separator after top picks
            const isTopSep = (topPicks.length > 0 && i === topPicks.length);
            // Separator after region priority
            const isRegionSep = (priority.length > 0 && i === topPicks.length + priority.length);
            const sep = (isTopSep || isRegionSep) ? ' data-separator="true"' : '';
            const bold = option.isTopPick ? ' data-top-pick="true"' : '';
            const tags = option.tags ? ` data-tags="${escapeHtml(option.tags)}"` : '';
            return `<option value="${escapeHtml(option.dial)}" data-country-code="${escapeHtml(option.iso)}"${tags}${sep}${bold}>${escapeHtml(option.label)} (${escapeHtml(option.dial)})</option>`;
        })
        .join('');
}


function getDefaultCountryOption() {
    // Priority 1: user's IP-detected country (their actual location — best for phone code)
    try {
        const ipCountry = (localStorage.getItem('newofficeplease_ip_country') || '').trim().toLowerCase();
        if (ipCountry && ipCountry !== 'undefined') {
            const match = COUNTRY_OPTIONS.find((option) => option.iso === ipCountry);
            if (match) return match;
        }
    } catch(e) {}

    // Priority 2: current entry's country (on detail pages)
    const entryCountry = (document.body.getAttribute('data-entry-country') || '').trim().toLowerCase();
    if (entryCountry) {
        const match = COUNTRY_OPTIONS.find((option) => option.iso === entryCountry);
        if (match) return match;
    }

    // Priority 3: site default
    const siteIso = getDefaultIso();
    return COUNTRY_OPTIONS.find((option) => option.iso === siteIso) || COUNTRY_OPTIONS[0];
}

/**
 * Get the user's country ISO from localStorage/cookie (no API call)
 */
function getUserCountryIso() {
    try {
        const stored = localStorage.getItem('newofficeplease_country_code');
        if (stored && stored !== 'undefined') {
            return stored.trim().toLowerCase();
        }
    } catch(e) {}
    return '';
}

function getEntryCountryIso() {
    // Get the country of the office entry, not the user's location
    // Priority 1: data-entry-country on body tag (set by detail page template)
    // Strip HTML tags in case the field contains markup (e.g. "<p>US</p>")
    const rawEntryCountry = (document.body.getAttribute('data-entry-country') || '').replace(/<[^>]*>/g, '').trim().toUpperCase();
    if (rawEntryCountry && rawEntryCountry.length === 2 && rawEntryCountry !== 'GB') return rawEntryCountry;
    // Note: GB is the hardcoded fallback in the template, so it may not be the real country

    // Priority 2: data-country-name on the CTA trigger (more reliable than country-code
    // which falls back to GB in the template)
    const trigger = document.querySelector('.btn-cta-primary[data-country-name]');
    if (trigger) {
        const name = (trigger.dataset.countryName || '').trim().toLowerCase();
        if (name) {
            // Map common country names to ISO codes
            const nameToIso = {
                'usa': 'US', 'united states': 'US', 'canada': 'CA', 'mexico': 'MX',
                'united kingdom': 'GB', 'uk': 'GB', 'england': 'GB',
                'south africa': 'ZA', 'nigeria': 'NG', 'kenya': 'KE', 'egypt': 'EG',
                'ghana': 'GH', 'morocco': 'MA', 'tanzania': 'TZ', 'uganda': 'UG',
                'india': 'IN', 'singapore': 'SG', 'hong kong': 'HK', 'japan': 'JP',
                'australia': 'AU', 'china': 'CN', 'philippines': 'PH', 'malaysia': 'MY',
                'thailand': 'TH', 'indonesia': 'ID', 'vietnam': 'VN', 'taiwan': 'TW',
                'south korea': 'KR', 'new zealand': 'NZ', 'pakistan': 'PK',
                'germany': 'DE', 'france': 'FR', 'spain': 'ES', 'italy': 'IT',
                'netherlands': 'NL', 'belgium': 'BE', 'switzerland': 'CH',
                'ireland': 'IE', 'portugal': 'PT', 'austria': 'AT', 'poland': 'PL',
                'sweden': 'SE', 'denmark': 'DK', 'norway': 'NO', 'finland': 'FI',
                'czech republic': 'CZ', 'romania': 'RO', 'hungary': 'HU',
                'greece': 'GR', 'turkey': 'TR', 'uae': 'AE', 'united arab emirates': 'AE',
                'saudi arabia': 'SA', 'qatar': 'QA', 'kuwait': 'KW', 'bahrain': 'BH',
                'israel': 'IL', 'jordan': 'JO', 'lebanon': 'LB',
                'brazil': 'BR', 'argentina': 'AR', 'chile': 'CL', 'colombia': 'CO',
            };
            const iso = nameToIso[name];
            if (iso) return iso;
        }
    }

    // Priority 3: if entryCountry was GB (the template fallback), still return it
    // as it's better than nothing
    if (rawEntryCountry && rawEntryCountry.length === 2) return rawEntryCountry;

    return '';
}

function getContextCountryIso() {
    // Priority 1: user's detected country from localStorage
    const userIso = getUserCountryIso();
    if (userIso) return userIso.toUpperCase();

    // Priority 2: currently selected country in dropdown
    const select = document.querySelector('.js-country-select');
    if (select) {
        const selected = select.options[select.selectedIndex];
        const code = (selected?.dataset.countryCode || '').toUpperCase();
        if (code) return code;
    }
    // Priority 3: site default
    return getDefaultIso().toUpperCase();
}

function getContextCity() {
    // Try to get city from the country selector's selected option text
    const select = document.querySelector('.js-country-select');
    if (select) {
        const name = select.options[select.selectedIndex]?.textContent?.trim();
        if (name) return name;
    }
    // Fall back to region name
    const region = document.querySelector('.site-title-region')?.textContent?.trim();
    return region || '';
}

function toDateTimeLocal(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function getInitialViewingDate() {
    const date = new Date();
    date.setHours(date.getHours() + 1, 0, 0, 0);

    if (date.getHours() > 20) {
        date.setDate(date.getDate() + 1);
        date.setHours(8, 0, 0, 0);
    } else if (date.getHours() < 8) {
        date.setHours(8, 0, 0, 0);
    }

    return date;
}

function renderViewingPicker(state, viewingState) {
    const picker = state.form.querySelector('[data-viewing-picker]');
    if (!picker) {
        return;
    }

    const monthLabel = picker.querySelector('[data-viewing-month]');
    const daysContainer = picker.querySelector('[data-viewing-days]');
    const timesContainer = picker.querySelector('[data-viewing-times]');
    const minDate = viewingState.minDate;
    const monthStart = new Date(viewingState.visibleMonth.getFullYear(), viewingState.visibleMonth.getMonth(), 1);
    const monthEnd = new Date(viewingState.visibleMonth.getFullYear(), viewingState.visibleMonth.getMonth() + 1, 0);
    const offset = monthStart.getDay();
    const totalCells = Math.ceil((offset + monthEnd.getDate()) / 7) * 7;

    monthLabel.textContent = viewingState.visibleMonth.toLocaleDateString(undefined, {
        month: 'long',
        year: 'numeric',
    });
    picker.classList.toggle('is-unconfirmed', !viewingState.hasExplicitSelection);

    // A time tapped before any date: ask for the date, in red, where the label used to be
    const datePrompt = picker.querySelector('[data-viewing-date-prompt]');
    if (datePrompt) {
        datePrompt.hidden = !(viewingState.timeTapped && !viewingState.dayChosen);
    }

    daysContainer.innerHTML = '';
    for (let index = 0; index < totalCells; index += 1) {
        const dayNumber = index - offset + 1;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'viewing-day';

        if (dayNumber < 1 || dayNumber > monthEnd.getDate()) {
            button.classList.add('is-empty');
            button.disabled = true;
            button.textContent = '';
            daysContainer.appendChild(button);
            continue;
        }

        const dayDate = new Date(viewingState.visibleMonth.getFullYear(), viewingState.visibleMonth.getMonth(), dayNumber, 12);
        const isBeforeMinDate = dayDate < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate(), 12);
        const isSelected = viewingState.selectedDay
            && dayDate.getFullYear() === viewingState.selectedDay.getFullYear()
            && dayDate.getMonth() === viewingState.selectedDay.getMonth()
            && dayDate.getDate() === viewingState.selectedDay.getDate();

        button.dataset.viewingDay = String(dayNumber);
        button.textContent = String(dayNumber);
        button.disabled = isBeforeMinDate;
        button.classList.toggle('is-selected', Boolean(isSelected));
        // A date the visitor actually tapped shows solid, even before a time confirms the slot
        button.classList.toggle('is-chosen', Boolean(isSelected && viewingState.dayChosen));
        daysContainer.appendChild(button);
    }

    timesContainer.innerHTML = '';
    buildViewingTimeSlots().forEach((time) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'viewing-time';
        button.dataset.viewingTime = time;
        button.textContent = time;

        const isDisabled = !viewingState.selectedDay || isTimeDisabled(viewingState.selectedDay, time, minDate);
        button.disabled = isDisabled;
        button.classList.toggle('is-selected', viewingState.selectedTime === time);
        button.classList.toggle('is-chosen', viewingState.selectedTime === time && Boolean(viewingState.timeTapped));
        timesContainer.appendChild(button);
    });

    layoutViewingTimes(picker, viewingState);
}

/**
 * Size the time strip to a whole number of slots so no slot is ever cut off,
 * then show the page holding the selected (or first available) slot.
 */
function layoutViewingTimes(picker, viewingState) {
    const wrap = picker.querySelector('.viewing-times-wrap');
    const times = picker.querySelector('[data-viewing-times]');
    const chip = times?.querySelector('.viewing-time');
    if (!wrap || !times || !chip) {
        return;
    }

    const prev = wrap.querySelector('.viewing-time-scroll--prev');
    const next = wrap.querySelector('.viewing-time-scroll--next');
    const wrapGap = parseFloat(getComputedStyle(wrap).columnGap) || 0;
    const available = wrap.clientWidth - (prev?.offsetWidth || 0) - (next?.offsetWidth || 0) - wrapGap * 2;
    if (available <= 0) {
        return;
    }

    const gap = parseFloat(getComputedStyle(times).columnGap) || 0;
    const pitch = chip.offsetWidth + gap;
    const visible = Math.max(1, Math.floor((available + gap) / pitch));
    times.style.width = `${visible * pitch - gap}px`;
    viewingState.timePageSize = visible;
    viewingState.timePitch = pitch;

    const chips = Array.from(times.querySelectorAll('.viewing-time'));
    let target = chips.findIndex((item) => item.classList.contains('is-selected'));
    if (target < 0) {
        target = chips.findIndex((item) => !item.disabled);
    }
    const page = Math.floor(Math.max(target, 0) / visible);
    times.scrollLeft = page * visible * pitch;
    updateViewingTimeScrollState(picker);
}

function syncViewingSelection(state, viewingState) {
    const hiddenField = state.form.querySelector('#datetimepicker');
    if (!hiddenField) {
        return;
    }

    if (!viewingState.hasExplicitSelection || !viewingState.selectedDay || !viewingState.selectedTime) {
        hiddenField.value = '';
        updateViewingMessage(state, '');
        updateStageControls(state, state.currentStage);
        return;
    }

    const [hours, minutes] = viewingState.selectedTime.split(':').map(Number);
    const selectedDate = new Date(
        viewingState.selectedDay.getFullYear(),
        viewingState.selectedDay.getMonth(),
        viewingState.selectedDay.getDate(),
        hours,
        minutes,
        0,
        0,
    );

    hiddenField.value = toDateTimeLocal(selectedDate);
    updateViewingMessage(state, hiddenField.value);
}

function hasValidViewingSelection(state) {
    const viewingState = state.viewingState;
    return Boolean(
        viewingState
        && viewingState.hasExplicitSelection
        && viewingState.selectedDay
        && viewingState.selectedTime
        && isViewingTimeValid(viewingState, viewingState.minDate),
    );
}

function buildViewingTimeSlots() {
    return [
        '08:00', '09:00', '10:00', '11:00', '12:00',
        '13:00', '14:00', '15:00', '16:00', '17:00',
        '18:00', '19:00', '20:00',
    ];
}

function isTimeDisabled(selectedDay, time, minDate) {
    const [hours, minutes] = time.split(':').map(Number);
    const candidate = new Date(
        selectedDay.getFullYear(),
        selectedDay.getMonth(),
        selectedDay.getDate(),
        hours,
        minutes,
        0,
        0,
    );

    return candidate < minDate;
}

function isViewingTimeValid(viewingState, minDate) {
    if (!viewingState.selectedDay || !viewingState.selectedTime) {
        return false;
    }

    return !isTimeDisabled(viewingState.selectedDay, viewingState.selectedTime, minDate);
}

function formatViewingDate(date) {
    const day = date.getDate();
    const suffix = getOrdinalSuffix(day);
    const weekday = date.toLocaleDateString(undefined, { weekday: 'long' });
    const month = date.toLocaleDateString(undefined, { month: 'long' });
    const hour = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `${weekday} ${day}${suffix} ${month} @ ${hour}`;
}

function formatViewingDateShort(date) {
    const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' });
    const month = date.toLocaleDateString('en-GB', { month: 'short' });
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${weekday} ${date.getDate()} ${month} \u00b7 ${hours}:${minutes}`;
}

function getOrdinalSuffix(day) {
    if (day > 3 && day < 21) {
        return 'th';
    }

    switch (day % 10) {
    case 1:
        return 'st';
    case 2:
        return 'nd';
    case 3:
        return 'rd';
    default:
        return 'th';
    }
}

function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function escapeHtml(value) {
    return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
}

// ========== FORM STATE PERSISTENCE (sessionStorage) ==========
const FORM_STORAGE_KEY = 'oe_form_draft';
const FORM_STORAGE_TTL = 30 * 60 * 1000; // 30 minutes

// Fields to save/restore (by ID)
const PERSISTABLE_FIELDS = [
    'qq-email', 'qq-phone', 'qq-name', 'qq-surname', 'qq-company',
    'qq-question', 'qq-surname_callback', 'qq-company_callback'
];

function saveFormDraft() {
    if (!activeForm?.form || !activeForm.formType) return;

    const data = {
        type: activeForm.formType,
        ts: Date.now(),
        stage: activeForm.currentStage,
        fields: {},
        choices: {}
    };

    // Save text/email/tel inputs
    PERSISTABLE_FIELDS.forEach(id => {
        const el = activeForm.form.querySelector('#' + id);
        if (el && el.value.trim()) {
            data.fields[id] = el.value.trim();
        }
    });

    // Save phone country code
    const phoneSelect = activeForm.form.querySelector('#qq-country-code-select2-flags');
    if (phoneSelect) {
        data.fields['_phone_country'] = phoneSelect.value;
        data.fields['_phone_iso'] = phoneSelect.dataset.selectedIso || '';
    }

    // Save stage choices (the selected option buttons per stage)
    activeForm.form.querySelectorAll('.qq-stage').forEach(stage => {
        const stageNum = stage.dataset.qqStage;
        const selected = stage.querySelector('a[data-value].qq-selected');
        if (selected && stageNum) {
            data.choices[stageNum] = selected.dataset.value;
        }
    });

    // Save user-choice hidden fields (desks, term) but NOT page-context fields
    // Context fields (officeID, url, country, city, reference, etc.) must always
    // come from the current page, not a previous form session
    const CONTEXT_FIELDS = new Set([
        'officeID', 'officeInterest', 'url', 'country', 'city', 'reference',
        'csrf_token', 'browser', 'language', 'adword_referral', 'cta_source', 'enquiry',
        'website_url', 'telephone-combined', 'pu_date', 'ed_date', 'formType'
    ]);
    activeForm.form.querySelectorAll('input[type="hidden"][id]').forEach(input => {
        if (input.value.trim() && !CONTEXT_FIELDS.has(input.id)) {
            data.fields[input.id] = input.value.trim();
        }
    });

    // Save viewing date/time if set
    if (activeForm.viewingState?.hasExplicitSelection) {
        if (activeForm.viewingState.selectedDay) {
            data.fields['_viewing_day'] = activeForm.viewingState.selectedDay.toISOString();
        }
        if (activeForm.viewingState.selectedTime) {
            data.fields['_viewing_time'] = activeForm.viewingState.selectedTime;
        }
    }

    // Only save if there's meaningful data
    if (Object.keys(data.fields).length > 0 || Object.keys(data.choices).length > 0) {
        try { sessionStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(data)); } catch(e) {}
    }
}

function restoreFormDraft(state) {
    try {
        const raw = sessionStorage.getItem(FORM_STORAGE_KEY);
        if (!raw) return;

        const data = JSON.parse(raw);
        const form = state.form;

        // Check TTL
        if (Date.now() - data.ts > FORM_STORAGE_TTL) {
            sessionStorage.removeItem(FORM_STORAGE_KEY);
            return;
        }

        // Only restore if same form type
        if (data.type !== state.formType) return;

        // Restore text field values
        Object.entries(data.fields).forEach(([id, value]) => {
            if (id.startsWith('_')) return;
            const el = form.querySelector('#' + id);
            if (el) {
                el.value = value;
                const holder = el.closest('.qq-text-input, .qq-telephone-field-holder');
                if (holder) holder.classList.add('had-focus-permenant');
            }
        });

        // Restore stage choices (mark the selected option button)
        Object.entries(data.choices).forEach(([stageNum, value]) => {
            const stage = form.querySelector(`.qq-stage[data-qq-stage="${stageNum}"]`);
            if (!stage) return;
            stage.querySelectorAll('a[data-value]').forEach(link => link.classList.remove('qq-selected'));
            const match = stage.querySelector(`a[data-value="${CSS.escape(value)}"]`);
            if (match) {
                match.classList.add('qq-selected');
                // Also set the hidden target field
                const targetField = stage.dataset.targetField;
                if (targetField && targetField !== 'office-type') {
                    const targetInput = form.querySelector(`#${CSS.escape(targetField)}`);
                    if (targetInput) targetInput.value = value;
                }
            }
        });

        // Restore phone country code
        if (data.fields['_phone_country']) {
            const phoneSelect = form.querySelector('#qq-country-code-select2-flags');
            if (phoneSelect) {
                phoneSelect.value = data.fields['_phone_country'];
                phoneSelect.dataset.selectedIso = data.fields['_phone_iso'] || '';
                applyCountryFlag(phoneSelect, data.fields['_phone_iso'] || '');
                const trigger = phoneSelect.closest('.select2-country-flags')?.querySelector('.country-select-trigger');
                if (trigger) {
                    updateCountrySelectTrigger(trigger, data.fields['_phone_country'], data.fields['_phone_iso'] || '');
                }
            }
        }

        // Restore viewing date/time if saved
        if (data.fields['_viewing_day'] && state.viewingState) {
            state.viewingState.selectedDay = new Date(data.fields['_viewing_day']);
            state.viewingState.selectedTime = data.fields['_viewing_time'] || null;
            state.viewingState.hasExplicitSelection = Boolean(state.viewingState.selectedDay && state.viewingState.selectedTime);
            state.viewingState.visibleMonth = new Date(
                state.viewingState.selectedDay.getFullYear(),
                state.viewingState.selectedDay.getMonth(),
                1
            );
            renderViewingPicker(state, state.viewingState);
            syncViewingSelection(state, state.viewingState);
        }

        // Restore to the stage they were on
        if (data.stage && data.stage > state.startingStage) {
            activateStage(state, data.stage, { animateSplash: false });
        }
    } catch(e) {}
}

function clearFormDraft() {
    try { sessionStorage.removeItem(FORM_STORAGE_KEY); } catch(e) {}
}

function extractFormFields(form) {
    if (!form) return {};
    return {
        people:     form.querySelector('.qq-size-values a.qq-selected')?.dataset.value || '',
        officeType: form.querySelector('.qq-type-values a.qq-selected')?.dataset.label || '',
        duration:   form.querySelector('.qq-term-values a.qq-selected')?.dataset.value || '',
    };
}
// ========== END FORM STATE PERSISTENCE ==========

function resetFormState() {
    if (!activeForm) {
        return;
    }

    // Track abandonment on close — submission case is handled immediately in submitFormRequest
    if (activeForm._openedAt && !activeForm._submitted) {
        const timeInForm = Math.round((Date.now() - activeForm._openedAt) / 1000);
        const maxStage = activeForm._maxStage || activeForm.currentStage;
        const formFields = extractFormFields(activeForm.form);
        import('./cta-analytics.js').then(m => m.trackFormProgress(maxStage, timeInForm, true, formFields));
    }

    // Save form data before clearing (unless form was successfully submitted)
    if (!activeForm._submitted) {
        saveFormDraft();
    }

    window.clearInterval(activeForm.countdownTimer);
    window.clearTimeout(activeForm.splashTimer);

    activeForm.dialog?.classList.remove('form-dialog--wide');
    activeForm.dialog?.classList.remove('form-dialog--viewing');
    activeForm.dialog?.classList.remove('form-dialog--compact');
    activeForm.container?.classList.remove('get-details--dialog--compact');
    activeForm.content.innerHTML = '';
    activeForm = null;
}
